// src/pages/ManagerDashboard.js
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { app, auth, db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { orgDoc, getOrg } from "../utils/tenant";
import { Toast } from "../components/UIComponents";
import { useBoundStoreName } from "../hooks/useBoundStore";
import { useTheme } from "../theme/ThemeProvider";

import { formatDurationMinutes } from "../utils/durationFormat";
import { classifyResponseException } from "../utils/responseClassification";
import { NEO_BUILD_ID } from "../registerSW";
const getOrgId = () => getOrg()?.orgId || localStorage.getItem("orgId") || "";
const shiftsColl = (orgId, storeId) =>
  collection(db, "orgs", orgId, "stores", storeId, "shifts");

const leaveTypeLabel = (value) => ({
  annual: "Annual Leave",
  sick: "Sick Leave",
  family_responsibility: "Family Responsibility Leave",
  unpaid: "Unpaid Leave",
  other: "Other / Manual",
}[String(value || "").toLowerCase()] || "Leave");

const leaveTsDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const MANAGER_LEAVE_TYPES = {
  annual: { id: "annual-leave", label: "Annual Leave", paid: true, allocateHours: true, defaultHours: 8 },
  sick: { id: "sick-leave", label: "Sick Leave", paid: true, allocateHours: true, defaultHours: 8 },
  family_responsibility: { id: "family-responsibility-leave", label: "Family Responsibility Leave", paid: true, allocateHours: true, defaultHours: 8 },
  unpaid: { id: "unpaid-leave", label: "Unpaid Leave", paid: false, allocateHours: false, defaultHours: 0 },
  other: { id: "paid-leave", label: "Other / Manual", paid: true, allocateHours: true, defaultHours: 8 },
};
const managerLeaveDateKeys = (startDate, endDate) => {
  if (!startDate) return [];
  const start = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate || startDate}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
  const keys = [];
  const cursor = new Date(start);
  while (cursor <= end && keys.length < 370) {
    keys.push(`${cursor.getFullYear()}-${String(cursor.getMonth()+1).padStart(2,"0")}-${String(cursor.getDate()).padStart(2,"0")}`);
    cursor.setDate(cursor.getDate()+1);
  }
  return keys;
};
const managerShiftNetHours = (shiftValue, lunchMinutes = 0) => {
  const match = String(shiftValue || "").match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (!match) return 0;
  let start = Number(match[1])*60 + Number(match[2]);
  let end = Number(match[3])*60 + Number(match[4]);
  if (end <= start) end += 1440;
  return Math.max(0, (end-start-Math.max(0, Number(lunchMinutes)||0))/60);
};
const managerAttendanceValue = (id, hours) => `ATTENDANCE::${id}::${Math.max(0, Number(hours)||0)}`;

function formatClockTime(date) {
  if (!date) return "—";
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function pad2(value) { return String(value).padStart(2, "0"); }
function managerTodayKey(date = new Date()) { return `${date.getFullYear()}-${pad2(date.getMonth()+1)}-${pad2(date.getDate())}`; }
function managerMonthKey(date = new Date()) { return `${date.getFullYear()}-${pad2(date.getMonth()+1)}`; }
function managerTsToDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value === "number") return new Date(value > 100000000000 ? value : value * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
function managerScheduleDateTime(dateKey, timeText) {
  const dateParts = String(dateKey || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeParts = String(timeText || "").match(/^(\d{2}):(\d{2})$/);
  if (!dateParts || !timeParts) return null;
  return new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]), Number(timeParts[1]), Number(timeParts[2]), 0, 0);
}
function managerShiftLunchMinutes(shift, fallbackMinutes = 60) {
  const direct = Number(shift?.lunchMin ?? shift?.lunchMinutes ?? shift?.lunch);
  if (Number.isFinite(direct) && direct >= 0) return direct;
  if (typeof shift?.lunchTaken === "boolean") return shift.lunchTaken ? fallbackMinutes : 0;
  return fallbackMinutes;
}
function managerAttendanceHours(entry = {}, defaultLunchMinutes = 60) {
  const candidates = [entry.allocatedHours, entry.paidHours, entry.scheduledHours, entry.hours, entry.durationHours, entry.leaveHours, entry.attendanceHours];
  const direct = candidates.map(Number).find((value) => Number.isFinite(value) && value >= 0);
  if (direct !== undefined) return direct;
  const win = scheduleWindow(entry);
  let start = managerScheduleDateTime(entry.date, win.startTime);
  let end = managerScheduleDateTime(entry.date, win.endTime);
  if (!start || !end) return 0;
  if (end <= start) end = new Date(end.getTime() + 86400000);
  const raw = Math.max(0, (end.getTime() - start.getTime()) / 3600000);
  return Math.max(0, raw - Math.max(0, Number(defaultLunchMinutes) || 0) / 60);
}
function managerIsPaidAttendance(entry = {}) {
  const identity = `${entry.attendanceTypeId || ""} ${entry.attendanceLabel || ""} ${entry.leaveLabel || ""}`.toLowerCase();
  if (identity.includes("unpaid")) return false;
  if (entry.paid === false || entry.isPaid === false) return false;
  return identity.includes("leave") || entry.paid === true || entry.isPaid === true;
}
function managerFormatVarianceDuration(hours) {
  const totalMinutes = Math.round(Math.abs(Number(hours) || 0) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (!h) return `${m}m`;
  if (!m) return `${h}h`;
  return `${h}h ${m}m`;
}
function buildManagerScheduleVariance({ entries = [], shifts = [], staffName = "", now = new Date(), defaultLunchMinutes = 60 }) {
  let scheduledHoursToDate = 0;
  let totalPublishedScheduledHours = 0;
  let paidLeaveHoursToDate = 0;
  let creditedWorkedHoursToDate = 0;
  let approvedOvertimeHoursToDate = 0;
  let todayScheduledHours = 0;
  let todayCreditedWorkedHours = 0;
  let todayPaidLeaveHours = 0;
  const todayKey = managerTodayKey(now);
  const scheduleByDate = new Map();

  (entries || []).forEach((entry) => {
    const dateKey = String(entry?.date || "");
    if (dateKey) scheduleByDate.set(dateKey, entry);
    if (entry?.isAttendance === true || entry?.attendanceTypeId) {
      if (!dateKey || !managerIsPaidAttendance(entry)) return;
      const leaveHours = managerAttendanceHours(entry, defaultLunchMinutes);
      totalPublishedScheduledHours += leaveHours;
      if (dateKey > todayKey) return;
      scheduledHoursToDate += leaveHours;
      paidLeaveHoursToDate += leaveHours;
      if (dateKey === todayKey) { todayScheduledHours += leaveHours; todayPaidLeaveHours += leaveHours; }
      return;
    }
    if (entry?.off === true || String(entry?.publishedShift || "").trim().toUpperCase() === "OFF") return;
    const win = scheduleWindow(entry);
    let start = managerScheduleDateTime(dateKey, win.startTime);
    let end = managerScheduleDateTime(dateKey, win.endTime);
    if (!start || !end) return;
    if (end <= start) end = new Date(end.getTime() + 86400000);
    const fullRaw = Math.max(0, (end.getTime() - start.getTime()) / 3600000);
    const fullPaid = Math.max(0, fullRaw - Math.max(0, Number(defaultLunchMinutes) || 0) / 60);
    totalPublishedScheduledHours += fullPaid;
    if (now <= start) return;
    if (now < end) {
      const elapsed = Math.max(0, (now.getTime() - start.getTime()) / 3600000);
      scheduledHoursToDate += elapsed;
      if (dateKey === todayKey) todayScheduledHours += elapsed;
      return;
    }
    scheduledHoursToDate += fullPaid;
    if (dateKey === todayKey) todayScheduledHours += fullPaid;
  });

  const monthKey = managerMonthKey(now);
  const normalizedName = String(staffName || "").trim().toLowerCase();
  (shifts || []).forEach((shift) => {
    const shiftName = String(shift?.name || shift?.staffName || "").trim().toLowerCase();
    if (normalizedName && shiftName !== normalizedName) return;
    const clockIn = managerTsToDate(shift?.clockIn || shift?.clockInAt || shift?.in || shift?.inAt);
    if (!clockIn || managerMonthKey(clockIn) !== monthKey || clockIn > now) return;
    const dateKey = managerTodayKey(clockIn);
    const entry = scheduleByDate.get(dateKey);
    let actualEnd = managerTsToDate(shift?.clockOut || shift?.actualClockOut || shift?.thresholdClockOut || shift?.clockOutAt || shift?.out || shift?.outAt);
    const isLive = !actualEnd && dateKey === todayKey && (now.getTime() - clockIn.getTime()) <= 18 * 3600000;
    if (isLive) actualEnd = now;
    if (!actualEnd || actualEnd <= clockIn) return;

    const approved = shift?.scheduleVarianceApproved === true;
    const isAttendance = entry?.isAttendance === true || Boolean(entry?.attendanceTypeId);
    const isOff = entry?.off === true || String(entry?.publishedShift || "").trim().toUpperCase() === "OFF";
    if (!entry || isOff || isAttendance) {
      if (!approved) return;
      const raw = Math.max(0, (actualEnd.getTime() - clockIn.getTime()) / 3600000);
      const lunch = isLive ? 0 : managerShiftLunchMinutes(shift, defaultLunchMinutes) / 60;
      const credited = Math.max(0, raw - lunch);
      creditedWorkedHoursToDate += credited;
      approvedOvertimeHoursToDate += credited;
      if (dateKey === todayKey) todayCreditedWorkedHours += credited;
      return;
    }

    const win = scheduleWindow(entry);
    let scheduledStart = managerScheduleDateTime(dateKey, win.startTime);
    let scheduledEnd = managerScheduleDateTime(dateKey, win.endTime);
    if (!scheduledStart || !scheduledEnd) return;
    if (scheduledEnd <= scheduledStart) scheduledEnd = new Date(scheduledEnd.getTime() + 86400000);
    const creditStart = approved && clockIn < scheduledStart ? clockIn : new Date(Math.max(clockIn.getTime(), scheduledStart.getTime()));
    const rosterEndToNow = new Date(Math.min(scheduledEnd.getTime(), now.getTime()));
    const creditEnd = approved && actualEnd > scheduledEnd
      ? new Date(Math.min(actualEnd.getTime(), now.getTime()))
      : new Date(Math.min(actualEnd.getTime(), rosterEndToNow.getTime()));
    if (creditEnd <= creditStart) return;
    const raw = (creditEnd.getTime() - creditStart.getTime()) / 3600000;
    const lunch = isLive ? 0 : managerShiftLunchMinutes(shift, defaultLunchMinutes) / 60;
    const credited = Math.max(0, raw - lunch);
    creditedWorkedHoursToDate += credited;
    if (dateKey === todayKey) todayCreditedWorkedHours += credited;
    if (approved) {
      const regularStart = new Date(Math.max(clockIn.getTime(), scheduledStart.getTime()));
      const regularEnd = new Date(Math.min(actualEnd.getTime(), scheduledEnd.getTime(), now.getTime()));
      const regularRaw = regularEnd > regularStart ? (regularEnd.getTime() - regularStart.getTime()) / 3600000 : 0;
      const regularCredited = Math.max(0, regularRaw - lunch);
      approvedOvertimeHoursToDate += Math.max(0, credited - regularCredited);
    }
  });

  const creditedHours = creditedWorkedHoursToDate + paidLeaveHoursToDate;
  const varianceHours = creditedHours - scheduledHoursToDate;
  const remainingScheduledHours = Math.max(0, totalPublishedScheduledHours - scheduledHoursToDate);
  return {
    scheduledHoursToDate,
    totalPublishedScheduledHours,
    remainingScheduledHours,
    projectedMonthEndHours: creditedHours + remainingScheduledHours,
    paidLeaveHoursToDate,
    approvedOvertimeHoursToDate,
    regularCreditedHoursToDate: Math.max(0, creditedWorkedHoursToDate - approvedOvertimeHoursToDate),
    todayScheduledHours,
    todayCreditedHours: todayCreditedWorkedHours + todayPaidLeaveHours,
    creditedHours,
    varianceHours,
  };
}
function secondsForScheduleTime(dateKey, time) {
  const m = String(time || "").match(/^(\d{2}):(\d{2})$/);
  if (!dateKey || !m) return 0;
  const [y,mo,d] = dateKey.split("-").map(Number);
  return Math.floor(new Date(y,mo-1,d,Number(m[1]),Number(m[2]),0,0).getTime()/1000);
}
function firestoreSeconds(value) {
  if (!value) return null;
  if (typeof value?.seconds === "number") return value.seconds;
  if (typeof value?.toDate === "function") return Math.floor(value.toDate().getTime()/1000);
  if (typeof value === "number") return value > 100000000000 ? Math.floor(value/1000) : Math.floor(value);
  const d = new Date(value); return Number.isNaN(d.getTime()) ? null : Math.floor(d.getTime()/1000);
}
function scheduleWindow(entry = {}) {
  const publishedShift = String(entry.publishedShift || entry.shift || "").trim();
  const parsed = publishedShift.match(/^(\d{2}:\d{2})-(\d{2}:\d{2})$/);
  return { startTime: entry.startTime || parsed?.[1] || "", endTime: entry.endTime || parsed?.[2] || "", label: publishedShift || (entry.startTime && entry.endTime ? `${entry.startTime}-${entry.endTime}` : "—") };
}
function managerScheduleIntelligence({ entry = {}, actual = null, now = new Date(), graceMinutes = 10 }) {
  const dateKey = String(entry.date || managerTodayKey(now));
  const win = scheduleWindow(entry);
  const startSec = secondsForScheduleTime(dateKey, win.startTime);
  let endSec = secondsForScheduleTime(dateKey, win.endTime);
  if (startSec && endSec && endSec <= startSec) endSec += 86400;
  const nowSec = Math.floor(now.getTime()/1000);
  if (entry.isAttendance === true || entry.attendanceTypeId) return { label: entry.attendanceLabel || "Leave / attendance", tone: "#a78bfa" };
  if (entry.off === true || String(entry.publishedShift || "").toUpperCase() === "OFF") return actual ? { label: "Clocked in on an OFF day · approval required", tone: "#fbbf24" } : { label: "Off day", tone: "#94a3b8" };
  if (!actual) {
    if (startSec && nowSec > startSec + Number(graceMinutes || 0)*60) return { label: "Not clocked in", tone: "#fb7185" };
    if (startSec && nowSec < startSec) return { label: `Starts in ${formatDurationMinutes(Math.max(0, Math.round((startSec-nowSec)/60)))}`, tone: "#60a5fa" };
    return { label: "Awaiting attendance", tone: "#94a3b8" };
  }
  if (actual.scheduleVarianceApproved === true) return { label: `${String(actual.scheduleVarianceType || "").replace(/_/g," ") || "Exception"} · approved`, tone: "#34d399" };
  if (actual.scheduleVarianceRejected === true) return { label: `${String(actual.scheduleVarianceType || "").replace(/_/g," ") || "Exception"} · rejected`, tone: "#fb7185" };
  const inSec = Number(actual.clockInSec || 0), outSec = Number(actual.clockOutSec || 0);
  if (inSec && startSec) {
    const mins = Math.round((inSec-startSec)/60);
    const grace = Math.max(0, Number(graceMinutes || 0));
    if (mins < -grace) return { label: `${formatDurationMinutes(Math.abs(mins))} early overtime · approval required`, tone: "#38bdf8" };
    if (mins > grace) return { label: `${formatDurationMinutes(mins)} late`, tone: "#fbbf24" };
    if (mins < 0 && grace > 0) return { label: `On time · ${formatDurationMinutes(Math.abs(mins))} early within grace`, tone: "#34d399" };
    if (mins > 0 && grace > 0) return { label: `On time · ${formatDurationMinutes(mins)} late within grace`, tone: "#34d399" };
  }
  if (outSec && endSec) {
    const mins = Math.round((outSec-endSec)/60);
    if (mins > 0) return { label: `${formatDurationMinutes(mins)} overtime`, tone: "#38bdf8" };
    if (mins < 0) return { label: `${formatDurationMinutes(Math.abs(mins))} early clock-out`, tone: "#fbbf24" };
    return { label: "Completed on schedule", tone: "#34d399" };
  }
  if (!outSec && endSec && nowSec > endSec) return { label: `${formatDurationMinutes(Math.max(1,Math.round((nowSec-endSec)/60)))} overtime · approval required`, tone: "#38bdf8" };
  return { label: "On time", tone: "#34d399" };
}

function getBreakMinutes(value) {
  const date = value?.toDate
    ? value.toDate()
    : value?.seconds
    ? new Date(value.seconds * 1000)
    : value
    ? new Date(value)
    : null;

  if (!date) return 0;
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
}


function neoTsDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dutyManagerState(item = {}, now = new Date()) {
  if (item.completed === true || item.status === "completed") return "completed";
  const managerStatus = String(item?.managerEscalation?.status || "").toLowerCase();
  if (["queued", "sent", "failed", "waiting_for_manager"].includes(managerStatus)) return "escalated";
  const due = neoTsDate(item.dueAt);
  if (!due) return "upcoming";
  if (due.getTime() > now.getTime()) return "upcoming";
  const graceMins = Math.max(0, Number(item.gracePeriodMins || item.escalateAfterMins || 0));
  return now.getTime() > due.getTime() + graceMins * 60000 ? "overdue" : "due";
}

function managerDutyTimeLabel(item = {}, now = new Date()) {
  const due = neoTsDate(item.dueAt);
  if (!due) return item.dueTime || "—";
  const diffMins = Math.floor((now.getTime() - due.getTime()) / 60000);
  if (diffMins > 0) return `${formatDurationMinutes(diffMins)} overdue`;
  if (diffMins === 0) return "Due now";
  return `Due in ${formatDurationMinutes(Math.abs(diffMins))}`;
}

function buildManagerResponseRate(rows = []) {
  const completed = rows.filter((row) => row.completed === true || row.status === "completed");
  if (!completed.length) return 0;
  const onTime = completed.filter((row) => row.completionStatus !== "late").length;
  return Math.round((onTime / completed.length) * 100);
}


function formatNeoEventTime(value) {
  const d = neoTsDate(value);
  if (!d) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function shiftScheduleStart(shift = {}) {
  return neoTsDate(
    shift.scheduledStart ||
    shift.scheduleStart ||
    shift.scheduledClockIn ||
    shift.plannedStart ||
    shift.rosterStart
  );
}

function shiftScheduleEnd(shift = {}) {
  return neoTsDate(
    shift.scheduledEnd ||
    shift.scheduleEnd ||
    shift.scheduledClockOut ||
    shift.plannedEnd ||
    shift.rosterEnd
  );
}

function minutesLabel(value) {
  return formatDurationMinutes(value);
}

function shiftScheduleStatus(shift = {}) {
  const scheduledStart = shiftScheduleStart(shift);
  const scheduledEnd = shiftScheduleEnd(shift);
  const actualStart = neoTsDate(shift.clockIn);
  const actualEnd = neoTsDate(shift.clockOut);

  if (!scheduledStart && !scheduledEnd) {
    return {
      hasSchedule: false,
      label: shift.scheduleApprovalRequired === true || shift.approvalRequired === true
        ? "Approval required"
        : "Schedule not published",
      tone: shift.scheduleApprovalRequired === true || shift.approvalRequired === true ? "#fbbf24" : "#94a3b8",
      lateBy: 0,
      overtimeBy: 0,
      earlyBy: 0,
    };
  }

  const lateBy = scheduledStart && actualStart
    ? Math.max(0, Math.floor((actualStart.getTime() - scheduledStart.getTime()) / 60000))
    : 0;
  const overtimeBy = scheduledEnd && actualEnd
    ? Math.max(0, Math.floor((actualEnd.getTime() - scheduledEnd.getTime()) / 60000))
    : 0;
  const earlyBy = scheduledEnd && actualEnd
    ? Math.max(0, Math.floor((scheduledEnd.getTime() - actualEnd.getTime()) / 60000))
    : 0;

  if (shift.scheduleApprovalRequired === true || shift.approvalRequired === true || shift.unscheduled === true) {
    return { hasSchedule: true, label: "Approval required", tone: "#fbbf24", lateBy, overtimeBy, earlyBy };
  }
  if (lateBy > 0) return { hasSchedule: true, label: `Late ${minutesLabel(lateBy)}`, tone: "#fb7185", lateBy, overtimeBy, earlyBy };
  if (actualEnd && overtimeBy > 0) return { hasSchedule: true, label: `Overtime ${minutesLabel(overtimeBy)}`, tone: "#38bdf8", lateBy, overtimeBy, earlyBy };
  if (actualEnd && earlyBy > 0) return { hasSchedule: true, label: `Left early ${minutesLabel(earlyBy)}`, tone: "#fbbf24", lateBy, overtimeBy, earlyBy };
  if (actualEnd) return { hasSchedule: true, label: "Completed on time", tone: "#34d399", lateBy, overtimeBy, earlyBy };
  return { hasSchedule: true, label: "On schedule", tone: "#34d399", lateBy, overtimeBy, earlyBy };
}


function normalizeDutyCategory(item = {}) {
  const raw = [
    item.category,
    item.dutyCategory,
    item.standardCategory,
    item.sectionName,
    item.standardName,
    item.dutyLabel,
    item.label,
  ].filter(Boolean).join(" ").toLowerCase();

  if (/\bopening\b/.test(raw)) return "opening";
  if (/\bclosing\b/.test(raw)) return "closing";
  if (/\bhandover\b|\bhand over\b/.test(raw)) return "handover";
  if (/\bsecurity\b/.test(raw)) return "security";
  if (/\bcleaning\b|\bcleaner\b/.test(raw)) return "cleaning";
  if (/\bcash[\s-]?up\b|\bcashup\b/.test(raw)) return "cashup";
  return "custom";
}

function responseFilterLabel(key) {
  return {
    all: "All",
    opening: "Opening",
    closing: "Closing",
    handover: "Handover",
    security: "Security",
    cleaning: "Cleaning",
    cashup: "Cashup",
    custom: "Custom",
  }[key] || "All";
}

function startOfLocalDay(date = new Date()) {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
}

function isSameLocalDay(value, reference = new Date()) {
  const date = neoTsDate(value);
  if (!date) return false;
  return startOfLocalDay(date).getTime() === startOfLocalDay(reference).getTime();
}

function dutyOperationalDate(item = {}) {
  return (
    item.dueAt ||
    item.scheduledFor ||
    item.dueDate ||
    item.operationDate ||
    item.createdAt ||
    null
  );
}

function normaliseResponseKey(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

// Manager Response follows the current published Standard configuration.
// A queue item is response-worthy only while its source duty still has an alarm.
function collectAlarmDutyKeys(standards = []) {
  const keys = new Set();

  const addIdentity = (node = {}, parent = {}) => {
    [
      node.id, node.dutyId, node.checkId, node.key, node.name, node.label,
      node.dutyLabel, node.title, node.standardDutyId, node.sourceDutyId,
      parent.id, parent.standardId, parent.name, parent.standardName, parent.sectionName,
    ].forEach((value) => {
      const key = normaliseResponseKey(value);
      if (key) keys.add(key);
    });
  };

  const visit = (node, parent = {}) => {
    if (!node || typeof node !== "object") return;

    const alarmObject =
      (node.alarm && typeof node.alarm === "object" && node.alarm) ||
      (node.responseAlarm && typeof node.responseAlarm === "object" && node.responseAlarm) ||
      (node.alarmConfig && typeof node.alarmConfig === "object" && node.alarmConfig) ||
      null;

    const genericAlarmValues = Object.entries(node)
      .filter(([key]) => /alarm/i.test(key) && !["alarm", "responseAlarm", "alarmConfig"].includes(key))
      .map(([, value]) => value);

    const genericAlarmEnabled = genericAlarmValues.some((value) =>
      value === true ||
      (typeof value === "number" && value > 0) ||
      (typeof value === "string" && value.trim() && !["off", "false", "disabled", "none"].includes(value.trim().toLowerCase()))
    );

    const explicitlyEnabled =
      node.alarmEnabled === true ||
      node.responseAlarmEnabled === true ||
      node.hasAlarm === true ||
      node.alarmOn === true ||
      alarmObject?.enabled === true ||
      alarmObject?.active === true ||
      genericAlarmEnabled;

    const explicitlyDisabled =
      node.alarmEnabled === false ||
      node.responseAlarmEnabled === false ||
      node.hasAlarm === false ||
      node.alarmOn === false ||
      alarmObject?.enabled === false ||
      alarmObject?.active === false;

    const alarmHasSchedule = Boolean(
      node.alarmTime || node.responseAlarmTime || node.reminderTime ||
      alarmObject?.time || alarmObject?.at || alarmObject?.dueAt ||
      alarmObject?.minutesBefore || alarmObject?.offsetMinutes || alarmObject?.role || alarmObject?.recipientRole
    );

    if (!explicitlyDisabled && (explicitlyEnabled || (alarmObject && alarmHasSchedule))) {
      addIdentity(node, parent);
    }

    Object.entries(node).forEach(([key, value]) => {
      if (["alarm", "managerEscalation", "dueNotification"].includes(key)) return;
      if (Array.isArray(value)) value.forEach((child) => visit(child, node));
      else if (value && typeof value === "object" && typeof value?.toDate !== "function") visit(value, node);
    });
  };

  (standards || []).forEach((standard) => visit(standard));
  return keys;
}

function queueDutyMatchesAlarm(item = {}, alarmKeys = new Set()) {
  if (!alarmKeys?.size) return false;
  const candidates = [
    item.dutyId, item.sourceDutyId, item.standardDutyId, item.checkId,
    item.standardId, item.sourceStandardId, item.neoStandardId,
    item.dutyLabel, item.label, item.standardName, item.sectionName,
  ].map(normaliseResponseKey).filter(Boolean);
  return candidates.some((key) => alarmKeys.has(key));
}


function dutyAgeMinutes(item = {}, now = new Date()) {
  const due = neoTsDate(item.dueAt);
  if (!due) return 0;
  return Math.max(0, Math.floor((now.getTime() - due.getTime()) / 60000));
}

function summarizeExecutiveSignal({ attention = [], filteredRows = [], now = new Date() }) {
  const totalOpen = attention.length;
  const escalated = attention.filter((row) => dutyManagerState(row, now) === "escalated").length;
  const overdue = attention.filter((row) => dutyManagerState(row, now) === "overdue").length;

  const oldest = attention
    .slice()
    .sort((a, b) => dutyAgeMinutes(b, now) - dutyAgeMinutes(a, now))[0];

  const categoryCounts = {};
  attention.forEach((row) => {
    const cat = normalizeDutyCategory(row);
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  });

  const dominantCategoryEntry = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])[0] || ["custom", 0];

  const staffMap = new Map();
  filteredRows.forEach((row) => {
    const name = String(
      row.assignedToName ||
      row.completedBy ||
      row?.dueNotification?.recipientNames?.[0] ||
      ""
    ).trim();
    if (!name) return;

    const key = name.toLowerCase();
    const current = staffMap.get(key) || {
      name,
      assigned: 0,
      completed: 0,
      overdue: 0,
      escalated: 0,
    };

    current.assigned += 1;
    if (row.completed === true || row.status === "completed") current.completed += 1;
    const state = dutyManagerState(row, now);
    if (state === "overdue") current.overdue += 1;
    if (state === "escalated") current.escalated += 1;

    staffMap.set(key, current);
  });

  const staffSignals = Array.from(staffMap.values())
    .map((person) => ({
      ...person,
      completionRate: person.assigned ? Math.round((person.completed / person.assigned) * 100) : 0,
      riskScore: person.overdue * 2 + person.escalated * 3 + Math.max(0, person.assigned - person.completed),
    }))
    .sort((a, b) => b.riskScore - a.riskScore || a.completionRate - b.completionRate);

  const highestRiskStaff = staffSignals[0] || null;

  return {
    totalOpen,
    escalated,
    overdue,
    oldest,
    dominantCategory: dominantCategoryEntry[0],
    dominantCategoryCount: dominantCategoryEntry[1],
    highestRiskStaff,
  };
}

function getStyles(themeMode) {
  const dark = themeMode === "dark";

  return {
    page: {
      padding: 0,
      maxWidth: "100%",
      margin: 0,
      background: "transparent",
      color: dark ? "#f4f7fb" : "#0f2747",
    },
    surface: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    hero: {
      position: "relative",
      overflow: "hidden",
      borderRadius: 28,
      padding: "28px 28px 24px",
      background: dark
        ? "linear-gradient(135deg, rgba(4,18,43,0.98) 0%, rgba(3,17,42,0.98) 46%, rgba(2,10,26,0.98) 100%)"
        : "linear-gradient(135deg, rgba(245,248,253,0.98) 0%, rgba(239,244,251,0.98) 45%, rgba(232,239,249,0.98) 100%)",
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.28)"
        : "1px solid rgba(150, 174, 205, 0.28)",
      boxShadow: dark
        ? "0 24px 60px rgba(0,0,0,0.34)"
        : "0 20px 45px rgba(24, 55, 96, 0.08)",
    },
    heroGlow: {
      position: "absolute",
      inset: "auto auto -90px 30%",
      width: 260,
      height: 260,
      borderRadius: "50%",
      background: dark
        ? "radial-gradient(circle, rgba(65,147,255,0.22) 0%, rgba(65,147,255,0.08) 38%, rgba(65,147,255,0) 72%)"
        : "radial-gradient(circle, rgba(72,144,255,0.16) 0%, rgba(72,144,255,0.06) 38%, rgba(72,144,255,0) 72%)",
      pointerEvents: "none",
      filter: "blur(10px)",
    },
    topRow: {
      position: "relative",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 18,
      flexWrap: "wrap",
    },
    heroLeft: {
      minWidth: 260,
      flex: "1 1 420px",
    },
    heroRight: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 8,
      minWidth: 180,
      marginLeft: "auto",
    },
    badgeRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 18,
      flexWrap: "wrap",
      position: "relative",
      zIndex: 1,
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 14px",
      borderRadius: 999,
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.35)"
        : "1px solid rgba(150, 174, 205, 0.4)",
      background: dark ? "rgba(10, 29, 61, 0.72)" : "rgba(255,255,255,0.66)",
      color: dark ? "#74b3ff" : "#2f6ff2",
      fontSize: 12,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.14em",
    },
    themeButton: {
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.35)"
        : "1px solid rgba(150, 174, 205, 0.36)",
      background: dark ? "rgba(10, 29, 61, 0.76)" : "rgba(255,255,255,0.76)",
      color: dark ? "#dbe9ff" : "#12335f",
      borderRadius: 999,
      padding: "10px 14px",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: 700,
      boxShadow: dark
        ? "0 10px 28px rgba(0,0,0,0.22)"
        : "0 10px 22px rgba(35, 73, 120, 0.08)",
    },
    title: {
      margin: 0,
      fontSize: "clamp(2rem, 4vw, 3rem)",
      lineHeight: 1.04,
      fontWeight: 900,
      color: dark ? "#f4f7fb" : "#112c53",
    },
    titleAccent: {
      color: "#4f9cff",
      marginLeft: 10,
    },
    subtitle: {
      marginTop: 10,
      color: dark ? "rgba(223,233,248,0.78)" : "#607897",
      fontSize: 16,
      maxWidth: 560,
    },
    metaPills: {
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
      marginTop: 18,
    },
    metaPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 14px",
      borderRadius: 999,
      background: dark ? "rgba(8, 31, 63, 0.76)" : "rgba(255,255,255,0.82)",
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.26)"
        : "1px solid rgba(150, 174, 205, 0.3)",
      color: dark ? "#dbe7fb" : "#1c457a",
      fontSize: 13,
      fontWeight: 700,
    },
    liveTime: {
      fontSize: "clamp(2rem, 4vw, 3.3rem)",
      lineHeight: 0.95,
      fontWeight: 900,
      color: dark ? "#f4f7fb" : "#12335f",
      letterSpacing: "-0.04em",
    },
    liveSub: {
      color: dark ? "rgba(223,233,248,0.78)" : "#607897",
      fontSize: 14,
    },
    onlinePill: {
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 14px",
      borderRadius: 999,
      background: dark ? "rgba(25, 163, 116, 0.16)" : "rgba(63, 194, 135, 0.16)",
      border: "1px solid rgba(63, 194, 135, 0.25)",
      color: "#17a56e",
      fontWeight: 800,
      fontSize: 13,
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
      gap: 16,
    },
    statCard: {
      borderRadius: 24,
      padding: "20px 20px 18px",
      background: dark
        ? "linear-gradient(180deg, rgba(18,40,68,0.98) 0%, rgba(12,30,54,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,251,255,0.98) 100%)",
      border: dark
        ? "1px solid rgba(123, 186, 255, 0.22)"
        : "1px solid rgba(150, 174, 205, 0.22)",
      boxShadow: dark
        ? "0 22px 42px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(91,160,255,0.04)"
        : "0 16px 36px rgba(24, 55, 96, 0.08)",
    },
    statLabel: {
      fontSize: 12,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.12em",
      color: dark ? "#89aedf" : "#6d83a2",
      marginBottom: 12,
    },
    statValue: {
      fontSize: 34,
      fontWeight: 900,
      lineHeight: 1,
      color: dark ? "#f4f7fb" : "#0f2747",
      marginBottom: 8,
    },
    statSub: {
      color: dark ? "rgba(223,233,248,0.74)" : "#6c84a1",
      fontSize: 14,
    },
    panel: {
      borderRadius: 28,
      overflow: "hidden",
      background: dark
        ? "linear-gradient(180deg, rgba(18,40,68,0.98) 0%, rgba(12,30,54,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,250,254,0.98) 100%)",
      border: dark
        ? "1px solid rgba(123, 186, 255, 0.22)"
        : "1px solid rgba(150, 174, 205, 0.2)",
      boxShadow: dark
        ? "0 26px 52px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(91,160,255,0.04)"
        : "0 18px 40px rgba(24, 55, 96, 0.08)",
    },
    panelHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      padding: "22px 22px 18px",
      borderBottom: dark
        ? "1px solid rgba(67, 113, 181, 0.16)"
        : "1px solid rgba(150, 174, 205, 0.18)",
      flexWrap: "wrap",
    },
    panelTitle: {
      fontSize: 28,
      fontWeight: 900,
      color: dark ? "#f4f7fb" : "#112c53",
      margin: 0,
    },
    panelSub: {
      marginTop: 6,
      color: dark ? "rgba(223,233,248,0.72)" : "#6c84a1",
      fontSize: 14,
    },
    panelBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      borderRadius: 999,
      padding: "10px 14px",
      background: dark ? "rgba(25, 163, 116, 0.14)" : "rgba(63, 194, 135, 0.14)",
      border: "1px solid rgba(63, 194, 135, 0.24)",
      color: "#17a56e",
      fontWeight: 800,
      fontSize: 13,
    },
    tableWrap: {
      overflowX: "auto",
      padding: 18,
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      minWidth: 640,
    },
    th: {
      textAlign: "left",
      fontSize: 12,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.12em",
      color: dark ? "#8db4e6" : "#6d83a2",
      padding: "0 14px 12px",
      whiteSpace: "nowrap",
    },
    row: {
      background: dark ? "linear-gradient(180deg, rgba(22,45,77,0.98), rgba(14,31,54,0.98))" : "rgba(255,255,255,0.9)",
      boxShadow: dark
        ? "inset 0 0 0 1px rgba(123, 186, 255, 0.16), inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 18px rgba(0,0,0,0.16)"
        : "inset 0 0 0 1px rgba(150, 174, 205, 0.18)",
    },
    rowFirst: {
      borderTopLeftRadius: 18,
      borderBottomLeftRadius: 18,
    },
    rowLast: {
      borderTopRightRadius: 18,
      borderBottomRightRadius: 18,
    },
    td: {
      padding: "16px 14px",
      color: dark ? "#eff5ff" : "#173960",
      fontSize: 15,
      borderTop: dark
        ? "1px solid rgba(67, 113, 181, 0.08)"
        : "1px solid rgba(150, 174, 205, 0.12)",
      borderBottom: dark
        ? "1px solid rgba(67, 113, 181, 0.08)"
        : "1px solid rgba(150, 174, 205, 0.12)",
      verticalAlign: "middle",
    },
    nameCell: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      minWidth: 180,
    },
    avatar: {
      width: 40,
      height: 40,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      background: dark
        ? "linear-gradient(135deg, rgba(79,156,255,0.28) 0%, rgba(39,116,255,0.16) 100%)"
        : "linear-gradient(135deg, rgba(79,156,255,0.18) 0%, rgba(39,116,255,0.1) 100%)",
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.24)"
        : "1px solid rgba(150, 174, 205, 0.22)",
      color: "#4f9cff",
      fontWeight: 900,
      fontSize: 14,
      flexShrink: 0,
    },
    nameText: {
      fontWeight: 800,
      color: dark ? "#f4f7fb" : "#112c53",
    },
    mutedMeta: {
      fontSize: 12,
      color: dark ? "rgba(223,233,248,0.68)" : "#6d83a2",
      marginTop: 2,
    },
    rolePill: {
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 12px",
      borderRadius: 999,
      background: dark ? "rgba(24,48,82,0.96)" : "rgba(241,246,252,0.95)",
      border: dark
        ? "1px solid rgba(123, 186, 255, 0.18)"
        : "1px solid rgba(150, 174, 205, 0.24)",
      color: dark ? "#dbe7fb" : "#1b467f",
      fontSize: 13,
      fontWeight: 700,
    },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 12px",
      borderRadius: 999,
      background: dark ? "rgba(25, 163, 116, 0.14)" : "rgba(63, 194, 135, 0.14)",
      border: "1px solid rgba(63, 194, 135, 0.24)",
      color: "#17a56e",
      fontWeight: 800,
      fontSize: 12,
      whiteSpace: "nowrap",
    },
    statusPillBreak: {
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 12px",
      borderRadius: 999,
      background: dark ? "rgba(245, 158, 11, 0.16)" : "rgba(251, 191, 36, 0.14)",
      border: "1px solid rgba(245, 158, 11, 0.26)",
      color: dark ? "#ffbf47" : "#c77800",
      fontWeight: 800,
      fontSize: 12,
      whiteSpace: "nowrap",
    },
    rowMetaWarn: {
      fontSize: 12,
      color: dark ? "#ffbf47" : "#c77800",
      fontWeight: 800,
    },
    rowMetaSoft: {
      fontSize: 12,
      color: dark ? "rgba(223,233,248,0.74)" : "#6d83a2",
      lineHeight: 1.35,
      maxWidth: 280,
      wordBreak: "break-word",
    },
    responseGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: 14,
      padding: 18,
    },
    responseCard: {
      borderRadius: 20,
      padding: 16,
      background: dark
        ? "linear-gradient(180deg, rgba(21,43,72,.96), rgba(12,29,52,.96))"
        : "linear-gradient(180deg, rgba(255,255,255,.98), rgba(246,250,255,.98))",
      border: dark ? "1px solid rgba(123,186,255,.18)" : "1px solid rgba(150,174,205,.22)",
      boxShadow: dark ? "0 14px 30px rgba(0,0,0,.18)" : "0 12px 26px rgba(24,55,96,.07)",
    },
    responseMetricLabel: {
      color: dark ? "#8db4e6" : "#6d83a2",
      fontSize: 11,
      fontWeight: 900,
      textTransform: "uppercase",
      letterSpacing: ".1em",
    },
    responseMetricValue: {
      color: dark ? "#f4f7fb" : "#112c53",
      fontSize: 30,
      fontWeight: 950,
      marginTop: 5,
    },
    attentionList: {
      display: "grid",
      gap: 10,
      padding: 18,
    },
    attentionCard: {
      borderRadius: 18,
      padding: 14,
      background: dark ? "rgba(9,26,49,.86)" : "rgba(255,255,255,.94)",
      border: dark ? "1px solid rgba(251,113,133,.20)" : "1px solid rgba(225,29,72,.16)",
      cursor: "pointer",
      transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
    },
    dutyTitle: {
      color: dark ? "#f8fafc" : "#112c53",
      fontSize: 15,
      fontWeight: 900,
    },
    dutyMeta: {
      color: dark ? "rgba(223,233,248,.66)" : "#6d83a2",
      fontSize: 12,
      marginTop: 4,
    },
    responseChip: {
      display: "inline-flex",
      alignItems: "center",
      borderRadius: 999,
      padding: "6px 9px",
      fontSize: 11,
      fontWeight: 900,
      border: "1px solid rgba(251,113,133,.24)",
      background: "rgba(244,63,94,.10)",
      color: dark ? "#fda4af" : "#be123c",
    },
    emptyWrap: {
      padding: "36px 24px 30px",
      textAlign: "center",
      color: dark ? "rgba(223,233,248,0.78)" : "#607897",
    },
    emptyTitle: {
      fontSize: 20,
      fontWeight: 800,
      color: dark ? "#f4f7fb" : "#112c53",
      marginBottom: 8,
    },
    actions: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: 16,
    },
    actionButton: {
      border: "none",
      borderRadius: 22,
      padding: "18px 18px",
      cursor: "pointer",
      textAlign: "left",
      fontWeight: 800,
      boxShadow: dark
        ? "0 18px 38px rgba(0,0,0,0.22)"
        : "0 16px 34px rgba(24, 55, 96, 0.08)",
    },
    actionTitle: {
      fontSize: 20,
      fontWeight: 900,
      marginBottom: 6,
    },
    actionSub: {
      fontSize: 14,
      opacity: 0.92,
      lineHeight: 1.45,
    },
    reports: {
      background: dark
        ? "linear-gradient(135deg, rgba(18,79,179,0.98) 0%, rgba(47,122,255,0.98) 100%)"
        : "linear-gradient(135deg, rgba(43,110,232,0.98) 0%, rgba(80,151,255,0.98) 100%)",
      color: "#ffffff",
    },
    settings: {
      background: dark
        ? "linear-gradient(135deg, rgba(13,122,92,0.98) 0%, rgba(33,186,135,0.98) 100%)"
        : "linear-gradient(135deg, rgba(22,163,116,0.96) 0%, rgba(49,198,145,0.96) 100%)",
      color: "#ffffff",
    },
    back: {
      background: dark
        ? "linear-gradient(135deg, rgba(8,30,61,0.98) 0%, rgba(13,52,100,0.98) 100%)"
        : "linear-gradient(135deg, rgba(230,239,251,0.98) 0%, rgba(218,232,250,0.98) 100%)",
      color: dark ? "#f4f7fb" : "#12335f",
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.2)"
        : "1px solid rgba(150, 174, 205, 0.22)",
    },
  };
}

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const orgId = useMemo(() => getOrgId(), []);
  const { themeMode, toggleTheme } = useTheme();
  const styles = useMemo(() => getStyles(themeMode), [themeMode]);

  const boundStoreName = useBoundStoreName();
  const storeId = localStorage.getItem("storeId") || "";

  const initialName =
    boundStoreName ||
    localStorage.getItem("storeName") ||
    localStorage.getItem("storeLabel") ||
    "…";

  const [storeName, setStoreName] = useState(initialName);
  const [activeShifts, setActiveShifts] = useState([]);
  const [allStoreShifts, setAllStoreShifts] = useState([]);
  const [todayAttendanceShifts, setTodayAttendanceShifts] = useState([]);
  const [todayScheduleEntries, setTodayScheduleEntries] = useState([]);
  const [scheduleGraceMinutes, setScheduleGraceMinutes] = useState(10);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [activeStaffNameSet, setActiveStaffNameSet] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "info" });
  const [now, setNow] = useState(new Date());
  const [viewportWidth, setViewportWidth] = useState(() => typeof window !== "undefined" ? window.innerWidth : 1280);
  const isCompact = viewportWidth <= 900;
  const isMobile = viewportWidth <= 620;
  const [dutyRows, setDutyRows] = useState([]);
  const [publishedStandards, setPublishedStandards] = useState([]);
  const [dutyLoading, setDutyLoading] = useState(true);
  const [selectedDutyId, setSelectedDutyId] = useState("");
  const [responseDeskTab, setResponseDeskTab] = useState("needsDecision");
  const [responseHistoryOpen, setResponseHistoryOpen] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(true);
  const [selectedLeaveId, setSelectedLeaveId] = useState("");
  const [leaveDecisionState, setLeaveDecisionState] = useState({});
  const [leaveDecisionNote, setLeaveDecisionNote] = useState("");
  const [managerTab, setManagerTab] = useState("response");
  const [selectedScheduleRowId, setSelectedScheduleRowId] = useState("");
  const [managerVarianceEntries, setManagerVarianceEntries] = useState([]);
  const [managerVarianceShifts, setManagerVarianceShifts] = useState([]);
  const [managerVarianceLoading, setManagerVarianceLoading] = useState(false);
  const [managerVarianceError, setManagerVarianceError] = useState("");
  const [managerMonthlyTarget, setManagerMonthlyTarget] = useState(180);
  const [managerDefaultLunchMinutes, setManagerDefaultLunchMinutes] = useState(60);
  const [scheduleDecisionState, setScheduleDecisionState] = useState({});
  const [scheduleHistoryOpen, setScheduleHistoryOpen] = useState(false);
  const [scheduleHistoryRows, setScheduleHistoryRows] = useState([]);
  const [scheduleHistoryLoading, setScheduleHistoryLoading] = useState(false);
  const [currentScheduleOpen, setCurrentScheduleOpen] = useState(false);
  const [currentScheduleEntries, setCurrentScheduleEntries] = useState([]);
  const [currentScheduleLoading, setCurrentScheduleLoading] = useState(false);
  const [currentScheduleSelectedDate, setCurrentScheduleSelectedDate] = useState("");
  const currentScheduleRequestedDateRef = useRef("");
  const [scheduleIssues, setScheduleIssues] = useState([]);
  const [scheduleIssuesLoading, setScheduleIssuesLoading] = useState(false);
  const [scheduleIssueResponseDrafts, setScheduleIssueResponseDrafts] = useState({});
  const currentScheduleDateStripRef = useRef(null);
  const [expandedStaffId, setExpandedStaffId] = useState("");
  const [responseFilter, setResponseFilter] = useState(() => {
    try {
      return localStorage.getItem("neo_manager_response_filter") || "all";
    } catch {
      return "all";
    }
  });

  const showToast = useCallback((message, type = "info") => {
    setToast({ message, type });
  }, []);

  const managerActor = useMemo(() => {
    try {
      const session = JSON.parse(
        sessionStorage.getItem("mgrSession") ||
        localStorage.getItem("mgrSession") ||
        "{}"
      ) || {};
      return {
        uid: String(session.managerId || localStorage.getItem("managerId") || ""),
        name: String(session.managerName || localStorage.getItem("managerName") || "Manager"),
        email: String(session.managerEmail || ""),
        sessionToken: String(session.serverSessionToken || ""),
        sessionExpiresAt: Number(session.serverSessionExpiresAt || session.expiresAt || 0),
      };
    } catch {
      return {
        uid: String(localStorage.getItem("managerId") || ""),
        name: String(localStorage.getItem("managerName") || "Manager"),
        email: "",
        sessionToken: "",
        sessionExpiresAt: 0,
      };
    }
  }, []);

  const loadScheduleIssues = useCallback(async () => {
    if (!orgId || !storeId) return;
    setScheduleIssuesLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, "orgs", orgId, "neoScheduleIssues"),
        where("storeId", "==", storeId)
      ));
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .sort((a, b) => {
          const at = managerTsToDate(a.createdAt)?.getTime?.() || 0;
          const bt = managerTsToDate(b.createdAt)?.getTime?.() || 0;
          return bt - at;
        });
      setScheduleIssues(rows);
    } catch (error) {
      console.error("[manager] schedule issues load failed:", error);
      showToast("Could not load schedule issues.", "error");
    } finally {
      setScheduleIssuesLoading(false);
    }
  }, [orgId, storeId, showToast]);

  const updateScheduleIssueStatus = useCallback(async (issue, nextStatus) => {
    if (!issue?.id || !orgId) return;
    const responseText = String(scheduleIssueResponseDrafts[issue.id] || "").trim();
    const patch = {
      status: nextStatus,
      managerResponse: responseText || (nextStatus === "acknowledged"
        ? "Management has received this schedule issue and is reviewing it."
        : "Management has reviewed this schedule issue."),
      updatedAt: serverTimestamp(),
      updatedByUid: managerActor.uid || "",
      updatedByName: managerActor.name || "Manager",
    };
    if (nextStatus === "acknowledged") {
      patch.acknowledgedAt = serverTimestamp();
      patch.acknowledgedByName = managerActor.name || "Manager";
    }
    if (nextStatus === "resolved") {
      patch.resolvedAt = serverTimestamp();
      patch.resolvedByName = managerActor.name || "Manager";
    }
    try {
      await updateDoc(doc(db, "orgs", orgId, "neoScheduleIssues", issue.id), patch);
      setScheduleIssues((rows) => rows.map((row) => row.id === issue.id ? { ...row, ...patch, updatedAt: new Date(), ...(nextStatus === "resolved" ? { resolvedAt: new Date() } : {}), ...(nextStatus === "acknowledged" ? { acknowledgedAt: new Date() } : {}) } : row));
      setScheduleIssueResponseDrafts((drafts) => ({ ...drafts, [issue.id]: "" }));
      showToast(nextStatus === "resolved" ? "Schedule issue resolved ✓" : "Schedule issue acknowledged ✓", "success");
    } catch (error) {
      console.error("[manager] schedule issue update failed:", error);
      showToast("Could not update the schedule issue.", "error");
    }
  }, [orgId, managerActor, scheduleIssueResponseDrafts, showToast]);

  const openCurrentSchedule = useCallback(() => {
    const todayKey = managerTodayKey(new Date());
    currentScheduleRequestedDateRef.current = "";
    setCurrentScheduleSelectedDate(todayKey);
    setCurrentScheduleOpen(true);
  }, []);

  const openScheduleIssueDate = useCallback((issue) => {
    const issueDate = String(issue?.date || "").trim();
    currentScheduleRequestedDateRef.current = /^\d{4}-\d{2}-\d{2}$/.test(issueDate) ? issueDate : "";
    if (currentScheduleRequestedDateRef.current) {
      setCurrentScheduleSelectedDate(currentScheduleRequestedDateRef.current);
    }
    setCurrentScheduleOpen(true);
  }, []);

  // Load once for the active store so the Schedule tab can show its open-issue badge
  // before the manager opens the tab. Manual Refresh remains available in the Schedule panel.
  useEffect(() => {
    if (!orgId || !storeId) return;
    loadScheduleIssues();
  }, [orgId, storeId, loadScheduleIssues]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncViewport = () => setViewportWidth(window.innerWidth);
    syncViewport();
    window.addEventListener("resize", syncViewport, { passive: true });
    return () => window.removeEventListener("resize", syncViewport);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("neo_manager_response_filter", responseFilter);
    } catch {}
  }, [responseFilter]);


  /* ---------------- session guard ---------------- */
  useEffect(() => {
    if (!storeId) {
      navigate("/select-store", { replace: true });
      return;
    }
    const rawSess =
      sessionStorage.getItem("mgrSession") || localStorage.getItem("mgrSession");
    if (rawSess && !sessionStorage.getItem("mgrSession")) {
      sessionStorage.setItem("mgrSession", rawSess);
    }
    const hasSession = !!rawSess;
    if (!hasSession) navigate("/manager-login", { replace: true });
  }, [storeId, navigate]);

  /* ---------------- block archived managers ---------------- */
  useEffect(() => {
    const sess = JSON.parse(sessionStorage.getItem("mgrSession") || "{}") || {};
    const mgrId = sess?.managerId || "";
    if (!orgId || !storeId || !mgrId) return;

    (async () => {
      try {
        let data = null;
        const a = await getDoc(doc(db, "orgs", orgId, "cashup_managers", mgrId));
        if (a.exists()) data = a.data();
        if (!data) {
          const b = await getDoc(
            doc(db, "orgs", orgId, "stores", storeId, "managers", mgrId)
          );
          if (b.exists()) data = b.data();
        }
        const archived = data?.archived === true || data?.isArchived === true;
        if (archived) {
          sessionStorage.removeItem("mgrSession");
          navigate("/manager-login", { replace: true });
        }
      } catch {}
    })();
  }, [orgId, storeId, navigate]);

  /* ---------------- store name loader ---------------- */
  const fetchStoreName = useCallback(async () => {
    try {
      if (boundStoreName) {
        setStoreName(boundStoreName);
        return;
      }

      let snap = await getDoc(orgDoc(db, "cashup_stores", storeId));
      if (!snap.exists()) {
        snap = await getDoc(doc(db, "cashup_stores", storeId));
      }

      if (snap.exists()) {
        const storeData = snap.data() || {};
        const name = storeData?.name || storeData?.storeName || "Unknown";
        const target = Number(storeData?.monthlyTargetHours);
        if (Number.isFinite(target) && target > 0) setManagerMonthlyTarget(target);
        setStoreName(name);
        try {
          localStorage.setItem("storeName", name);
        } catch {}
      } else {
        setStoreName(
          localStorage.getItem("storeName") ||
            localStorage.getItem("storeLabel") ||
            "Unknown"
        );
      }
    } catch {
      setStoreName(
        localStorage.getItem("storeName") ||
          localStorage.getItem("storeLabel") ||
          "Unknown"
      );
      showToast("Failed to load store name", "error");
    }
  }, [storeId, boundStoreName, showToast]);

  useEffect(() => {
    if (storeId) fetchStoreName();
  }, [storeId, fetchStoreName]);

  /* ---------------- live staff list (non-archived) ---------------- */
  useEffect(() => {
    if (!orgId || !storeId) return undefined;

    const staffRef = collection(db, "orgs", orgId, "cashup_users");
    const staffQ = query(staffRef, where("storeId", "==", storeId));

    const unsub = onSnapshot(
      staffQ,
      (snap) => {
        const set = new Set();
        snap.docs.forEach((d) => {
          const u = d.data() || {};
          const isArchived = u.archived === true || u.isArchived === true;
          if (!isArchived) {
            const nm = String(u.name || "").trim();
            if (nm) set.add(nm.toLowerCase());
          }
        });
        setActiveStaffNameSet(set);
      },
      () => setActiveStaffNameSet(new Set())
    );

    return () => unsub();
  }, [orgId, storeId]);

  /* ---------------- leave intelligence ---------------- */
  // C7 cost guardrail: leave is decision intelligence, not a live operational feed.
  // Keep it asleep until the manager actually opens Leave Intelligence, then fetch
  // the active store once. Event-driven WhatsApp/email still handles new-request alerts.
  const loadLeaveRequests = useCallback(async () => {
    if (!orgId || !storeId) {
      setLeaveRequests([]);
      setLeaveLoading(false);
      return;
    }
    setLeaveLoading(true);
    try {
      const snap = await getDocs(collection(db, "orgs", orgId, "stores", storeId, "leaveRequests"));
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .sort((a, b) => {
          const aMs = leaveTsDate(a.submittedAt)?.getTime?.() || 0;
          const bMs = leaveTsDate(b.submittedAt)?.getTime?.() || 0;
          return bMs - aMs;
        });
      setLeaveRequests(rows);
    } catch (error) {
      console.error("[Neo Leave] Could not load leave requests", error);
      setLeaveRequests([]);
      showToast("Could not load leave requests.", "error");
    } finally {
      setLeaveLoading(false);
    }
  }, [orgId, storeId, showToast]);

  useEffect(() => {
    if (managerTab !== "leave") return;
    loadLeaveRequests();
  }, [managerTab, loadLeaveRequests]);

  const decideLeaveRequest = useCallback(async (request, approved) => {
    if (!request?.id || !orgId || !storeId) return;
    const nextStatus = approved ? "approved" : "rejected";
    const sessionToken = String(managerActor.sessionToken || "");
    if (!sessionToken) {
      showToast("Your secure manager session is missing. Please return to Manager Login and sign in again.", "error");
      return;
    }

    setLeaveDecisionState((current) => ({ ...current, [request.id]: "saving" }));
    try {
      const functions = getFunctions(app, "us-central1");
      const decide = httpsCallable(functions, "decideNeoLeaveRequest");
      const result = await decide({
        sessionToken,
        orgId,
        storeId,
        requestId: request.id,
        decision: approved ? "approve" : "reject",
        managerNote: String(leaveDecisionNote || "").trim(),
      });

      if (result?.data?.ok !== true) throw new Error("Leave decision was not confirmed.");

      setLeaveDecisionState((current) => ({ ...current, [request.id]: nextStatus }));
      setLeaveRequests((rows) => rows.map((row) => row.id === request.id ? { ...row, status: nextStatus, managerNote: String(leaveDecisionNote || "").trim(), decidedAt: new Date() } : row));
      setSelectedLeaveId("");
      setLeaveDecisionNote("");
      showToast(`${leaveTypeLabel(request.leaveType)} ${nextStatus} for ${request.staffName || "staff member"}.`, "success");
    } catch (err) {
      setLeaveDecisionState((current) => ({ ...current, [request.id]: "error" }));
      showToast(err?.message || "Could not save leave decision.", "error");
    }
  }, [orgId, storeId, managerActor.sessionToken, leaveDecisionNote, showToast]);

  /* ---------------- live active shifts ---------------- */
  useEffect(() => {
    if (!orgId || !storeId) return undefined;

    // C4 cost guardrail: the manager Response surface is a TODAY/NOW screen.
    // Listen only to shifts that clocked in today instead of attaching a realtime
    // listener to the store's entire historical shift collection. Closed shifts
    // from today remain included, so attendance and clock-out state stay live.
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const tomorrowStart = new Date(todayStart);
    tomorrowStart.setDate(tomorrowStart.getDate() + 1);
    const qy = query(
      shiftsColl(orgId, storeId),
      where("clockIn", ">=", Timestamp.fromDate(todayStart)),
      where("clockIn", "<", Timestamp.fromDate(tomorrowStart))
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const allRows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((s) => s.archived !== true && s.isArchived !== true);
        setAllStoreShifts(allRows);
        const latestByName = {};

        snap.docs.forEach((d) => {
          const s = { id: d.id, ...(d.data() || {}) };
          if (!s.clockIn) return;
          if (s.archived === true || s.isArchived === true) return;

          const inMs = s.clockIn?.seconds
            ? s.clockIn.seconds * 1000
            : s.clockIn?.toDate
            ? s.clockIn.toDate().getTime()
            : typeof s.clockIn === "number"
            ? s.clockIn
            : null;

          if (!inMs) return;

          const ageHours = Math.max(0, (Date.now() - inMs) / 3600000);
          if (ageHours > 18) return;

          const nameKey = String(s.name || "Unknown");
          const prev = latestByName[nameKey];
          if (!prev || inMs > (prev._inMs || 0)) {
            latestByName[nameKey] = { ...s, _inMs: inMs };
          }
        });

        const nowMs = Date.now();
        const active = Object.values(latestByName)
          .filter((s) => !s.clockOut)
          .map((s) => {
            const diff = nowMs - s._inMs;
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const ciDate = new Date(s._inMs);
            return {
              ...s,
              clockInDate: ciDate,
              duration: `${h}h ${m}m`,
            };
          })
          .sort((a, b) => a._inMs - b._inMs);

        const filtered =
          activeStaffNameSet instanceof Set
            ? active.filter((s) => {
                const nm = String(s.name || "").trim().toLowerCase();
                return !nm || activeStaffNameSet.has(nm);
              })
            : active;

        const todayKey = managerTodayKey(new Date());
        setTodayAttendanceShifts(
          snap.docs.map((d) => {
            const data = { id: d.id, ...(d.data() || {}) };
            const clockInSec = firestoreSeconds(data.clockIn) ?? firestoreSeconds(data.clockInAt) ?? firestoreSeconds(data.in) ?? firestoreSeconds(data.inAt);
            if (!clockInSec || data.archived === true || data.isArchived === true) return null;
            if (managerTodayKey(new Date(clockInSec * 1000)) !== todayKey) return null;
            return { ...data, clockInSec, clockOutSec: firestoreSeconds(data.clockOut) ?? firestoreSeconds(data.clockOutAt) ?? firestoreSeconds(data.out) ?? firestoreSeconds(data.outAt) ?? null, staffId: data.staffId || data.userId || data.userUid || data.uid || data.employeeId || "", staffName: data.name || data.staffName || data.staffNameSnapshot || data.employeeName || data.displayName || "", roleName: data.roleName || data.role || "", storeId: data.storeId || storeId };
          }).filter(Boolean).sort((a,b)=>Number(a.clockInSec||0)-Number(b.clockInSec||0))
        );
        setActiveShifts(filtered);
      },
      () => showToast("Error loading shifts", "error")
    );

    return () => unsub();
  }, [orgId, storeId, showToast, activeStaffNameSet]);

  /* ---------------- Schedule Intelligence source of truth (MS1) ---------------- */
  useEffect(() => {
    if (!orgId || !storeId) { setTodayScheduleEntries([]); setScheduleLoading(false); return undefined; }
    setScheduleLoading(true);
    const qy = query(
      collection(db, "orgs", orgId, "neo_schedule_entries"),
      where("date", "==", managerTodayKey(new Date())),
      where("storeId", "==", storeId)
    );
    return onSnapshot(qy, (snap) => {
      const rows = snap.docs.map((d)=>({id:d.id,...(d.data()||{})}))
        .filter((row)=>String(row.storeId||"")===String(storeId))
        .filter((row)=>row.published===true && String(row.draftStatus||"")==="published")
        .sort((a,b)=>(scheduleWindow(a).startTime||"99:99").localeCompare(scheduleWindow(b).startTime||"99:99") || String(a.staffName||"").localeCompare(String(b.staffName||"")));
      setTodayScheduleEntries(rows); setScheduleLoading(false);
    }, (error)=>{ console.error("[Neo Schedule] Could not load published schedule", error); setTodayScheduleEntries([]); setScheduleLoading(false); });
  }, [orgId, storeId]);

  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    // C7: labor/schedule settings are configuration. Fresh on page entry is enough;
    // they do not justify a permanent realtime listener on every manager dashboard.
    (async () => {
      try {
        const snap = await getDoc(doc(db, "orgs", orgId, "settings", "labor"));
        if (cancelled) return;
        const data = snap.data() || {};
        const value = Number(data?.scheduleGraceMinutes);
        if ([0,5,10,15,30].includes(value)) setScheduleGraceMinutes(value);
        const lunch = Number(data?.defaultLunchMinutes);
        if (Number.isFinite(lunch) && lunch >= 0) setManagerDefaultLunchMinutes(lunch);
        const target = Number(data?.monthlyTargetHours ?? data?.targetMonthlyHours);
        if (Number.isFinite(target) && target > 0) setManagerMonthlyTarget(target);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  /* ---------------- MS5: read-only current published schedule ---------------- */
  useEffect(() => {
    if (!currentScheduleOpen || !orgId || !storeId) return undefined;

    const nowDate = new Date();
    const todayKey = managerTodayKey(nowDate);
    const deepLinkDate = String(currentScheduleRequestedDateRef.current || "");
    const requestedDate = /^\d{4}-\d{2}-\d{2}$/.test(deepLinkDate)
      ? deepLinkDate
      : String(currentScheduleSelectedDate || "");
    const requestedMonthKey = /^\d{4}-\d{2}-\d{2}$/.test(requestedDate) ? requestedDate.slice(0, 7) : "";
    const monthKey = requestedMonthKey || `${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, "0")}`;

    setCurrentScheduleLoading(true);

    const qy = query(
      collection(db, "orgs", orgId, "neo_schedule_entries"),
      where("monthKey", "==", monthKey)
    );

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(qy);
        if (cancelled) return;
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => String(row.storeId || "") === String(storeId))
          .filter(
            (row) =>
              row.published === true &&
              String(row.draftStatus || "") === "published" &&
              String(row.date || "").startsWith(monthKey)
          )
          .sort((a, b) => {
            const dateCompare = String(a.date || "").localeCompare(String(b.date || ""));
            if (dateCompare !== 0) return dateCompare;
            const aStart = scheduleWindow(a).startTime || "99:99";
            const bStart = scheduleWindow(b).startTime || "99:99";
            return (
              aStart.localeCompare(bStart) ||
              String(a.staffName || "").localeCompare(String(b.staffName || ""))
            );
          });

        setCurrentScheduleEntries(rows);

        const uniqueDates = [...new Set(rows.map((row) => String(row.date || "")).filter(Boolean))];
        const deepLinkedDate = String(currentScheduleRequestedDateRef.current || "");
        setCurrentScheduleSelectedDate((current) => {
          if (deepLinkedDate && uniqueDates.includes(deepLinkedDate)) {
            return deepLinkedDate;
          }
          if (current && uniqueDates.includes(current)) return current;
          if (uniqueDates.includes(todayKey)) return todayKey;
          return uniqueDates.find((date) => date >= todayKey) || uniqueDates[0] || "";
        });
        if (deepLinkedDate && uniqueDates.includes(deepLinkedDate)) {
          currentScheduleRequestedDateRef.current = "";
        }

        setCurrentScheduleLoading(false);
      } catch (error) {
        if (cancelled) return;
        console.error("[Neo MS5] Could not load current published schedule", error);
        setCurrentScheduleEntries([]);
        setCurrentScheduleSelectedDate("");
        setCurrentScheduleLoading(false);
        showToast("Could not load the current published schedule.", "error");
      }
    })();

    return () => { cancelled = true; };
  }, [currentScheduleOpen, orgId, storeId, showToast]);

  /* ---------------- current published response standards ---------------- */
  useEffect(() => {
    if (!orgId || !storeId) {
      setPublishedStandards([]);
      return undefined;
    }

    const ref = collection(db, "orgs", orgId, "neoStandards");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => {
            const status = String(row.status || "").toLowerCase();
            const publishState = String(row.publishState || "").toLowerCase();
            if (status === "archived" || row.archived === true || row.isArchived === true) return false;
            const published = row.published === true || row.isPublished === true || status === "published" || publishState === "published";
            if (!published) return false;

            const ids = [
              row.storeId,
              ...(Array.isArray(row.storeIds) ? row.storeIds : []),
              ...(Array.isArray(row.stores) ? row.stores.map((store) => typeof store === "object" ? (store.id || store.storeId) : store) : []),
            ].filter(Boolean).map(String);
            return ids.length === 0 || ids.includes(String(storeId));
          });
        setPublishedStandards(rows);
      },
      () => setPublishedStandards([])
    );
    return () => unsub();
  }, [orgId, storeId]);

  /* ---------------- live Neo response duties ---------------- */
  useEffect(() => {
    if (!orgId || !storeId) {
      setDutyRows([]);
      setDutyLoading(false);
      return undefined;
    }

    setDutyLoading(true);
    // Phase 8: query only the bound store. Firestore rules can then prove that
    // a kiosk/manager dashboard is reading only its own site's response queue.
    const ref = query(
      collection(db, "orgs", orgId, "neoDutyQueue"),
      where("storeId", "==", storeId)
    );
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => !row.storeId || row.storeId === storeId)
          .filter((row) => row.status !== "removed")
          .sort((a, b) => (neoTsDate(a.dueAt)?.getTime?.() || 0) - (neoTsDate(b.dueAt)?.getTime?.() || 0));
        setDutyRows(rows);
        setDutyLoading(false);
      },
      async (error) => {
        const code = String(error?.code || "unknown");
        const message = String(error?.message || "Unknown Firestore error");

        console.error("[Neo Response] neoDutyQueue listener failed", {
          code,
          message,
          orgId,
          storeId,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
        });

        // Safari/iPad can occasionally fail the realtime transport while a
        // normal Firestore read still works. Try a one-shot read so the
        // Response tab remains usable and the surfaced error tells us whether
        // this is permissions/auth or only the realtime listener transport.
        try {
          const snap = await getDocs(ref);
          const rows = snap.docs
            .map((d) => ({ id: d.id, ...(d.data() || {}) }))
            .filter((row) => !row.storeId || row.storeId === storeId)
            .filter((row) => row.status !== "removed")
            .sort((a, b) => (neoTsDate(a.dueAt)?.getTime?.() || 0) - (neoTsDate(b.dueAt)?.getTime?.() || 0));

          setDutyRows(rows);
          setDutyLoading(false);
          showToast(`Neo live sync failed (${code}) — loaded current duties`, "warning");
          return;
        } catch (fallbackError) {
          const fallbackCode = String(fallbackError?.code || "unknown");
          const fallbackMessage = String(fallbackError?.message || "Unknown Firestore error");

          console.error("[Neo Response] neoDutyQueue fallback read failed", {
            code: fallbackCode,
            message: fallbackMessage,
            orgId,
            storeId,
          });

          setDutyRows([]);
          setDutyLoading(false);
          showToast(`Could not load Neo response duties — ${fallbackCode}`, "error");
        }
      }
    );
    return () => unsub();
  }, [orgId, storeId, showToast]);

  /* ---------------- toast auto-dismiss ---------------- */
  useEffect(() => {
    if (!toast.message) return;
    const t = setTimeout(() => setToast({ message: "", type: "info" }), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  function clearManagerSession() {
    try {
      sessionStorage.removeItem("mgrSession");
      sessionStorage.removeItem("managerSession");
      localStorage.removeItem("isManager");
      localStorage.removeItem("managerId");
      localStorage.removeItem("managerName");
    } catch {}
  }

  const logoutAndGoLogin = useCallback(() => {
    clearManagerSession();
    navigate("/manager-login", { replace: true });
  }, [navigate]);

  const managerName =
    sessionStorage.getItem("managerName") ||
    localStorage.getItem("managerName") ||
    "Manager";

  const timeText = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const dateText = now.toLocaleDateString([], {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const onlineCount = activeShifts.length;

  const activeAlarmDutyKeys = useMemo(
    () => collectAlarmDutyKeys(publishedStandards),
    [publishedStandards]
  );

  // Response is a live operational surface: today's duties only, and only duties
  // whose alarms are still enabled in the current published Standard source.
  const todayResponseRows = useMemo(
    () => (dutyRows || []).filter((row) =>
      isSameLocalDay(dutyOperationalDate(row), now) &&
      queueDutyMatchesAlarm(row, activeAlarmDutyKeys)
    ),
    [dutyRows, activeAlarmDutyKeys, now]
  );

  const dutyGroups = useMemo(() => {
    const grouped = { upcoming: [], due: [], overdue: [], escalated: [], completed: [] };
    (todayResponseRows || []).forEach((row) => {
      const state = dutyManagerState(row, now);
      (grouped[state] || grouped.upcoming).push(row);
    });
    return grouped;
  }, [todayResponseRows, now]);

  const filteredDutyRows = useMemo(
    () => (todayResponseRows || []).filter((row) => responseFilter === "all" || normalizeDutyCategory(row) === responseFilter),
    [todayResponseRows, responseFilter]
  );

  const filteredDutyGroups = useMemo(() => {
    const grouped = { upcoming: [], due: [], overdue: [], escalated: [], completed: [] };
    (filteredDutyRows || []).forEach((row) => {
      const state = dutyManagerState(row, now);
      (grouped[state] || grouped.upcoming).push(row);
    });
    return grouped;
  }, [filteredDutyRows, now]);

  const responseClassifiedRows = useMemo(
    () => (filteredDutyRows || []).map((row) => ({
      row,
      ...classifyResponseException(row, now),
    })),
    [filteredDutyRows, now]
  );

  const responseBuckets = useMemo(() => ({
    needsDecision: responseClassifiedRows.filter(({ operationalAge, responseState }) => operationalAge !== "stale" && responseState === "needs_decision").map(({ row }) => row),
    watching: responseClassifiedRows.filter(({ operationalAge, responseState }) => operationalAge !== "stale" && responseState === "watching").map(({ row }) => row),
    resolved: responseClassifiedRows.filter(({ operationalAge, responseState }) => operationalAge !== "stale" && responseState === "resolved").map(({ row }) => row),
    carryOver: responseClassifiedRows.filter(({ operationalAge }) => operationalAge === "carry_over").map(({ row }) => row),
    stale: responseClassifiedRows.filter(({ operationalAge }) => operationalAge === "stale").map(({ row }) => row),
  }), [responseClassifiedRows]);

  const attentionDuties = useMemo(
    () => [...responseBuckets.needsDecision, ...responseBuckets.watching]
      .filter((row) => ["due", "overdue", "escalated"].includes(dutyManagerState(row, now)))
      .sort((a, b) => {
        const aDecision = classifyResponseException(a, now).responseState === "needs_decision" ? 1 : 0;
        const bDecision = classifyResponseException(b, now).responseState === "needs_decision" ? 1 : 0;
        if (aDecision !== bDecision) return bDecision - aDecision;
        return dutyAgeMinutes(b, now) - dutyAgeMinutes(a, now);
      })
      .slice(0, 12),
    [responseBuckets, now]
  );

  const escalationHistory = useMemo(
    () =>
      (filteredDutyRows || [])
        .filter((row) => {
          if (!(row?.managerEscalation?.status || row.escalationStatus)) return false;
          const at = row?.managerEscalation?.sentAt || row.escalationReadyAt || row.updatedAt;
          return isSameLocalDay(at, now);
        })
        .sort((a, b) => {
          const aTime = neoTsDate(a?.managerEscalation?.sentAt || a.escalationReadyAt || a.updatedAt)?.getTime?.() || 0;
          const bTime = neoTsDate(b?.managerEscalation?.sentAt || b.escalationReadyAt || b.updatedAt)?.getTime?.() || 0;
          return bTime - aTime;
        })
        .slice(0, 8),
    [filteredDutyRows, now]
  );

  const recentResponseActivity = useMemo(() => {
    const events = [];
    (filteredDutyRows || []).forEach((row) => {
      const label = row.dutyLabel || row.label || "Duty";
      const store = row.storeName || storeName;
      const completedAt = neoTsDate(row.completedAt || row.completionAt);
      if ((row.completed === true || row.status === "completed") && completedAt && isSameLocalDay(completedAt, now)) {
        events.push({
          id: `completed-${row.id}`,
          at: completedAt,
          type: "Completed",
          label,
          detail: `${row.completedByName || row.completedBy || "Staff"} completed • ${store}`,
          tone: "#34d399",
        });
      }
      const escalationAt = neoTsDate(row?.managerEscalation?.sentAt || row.escalationReadyAt);
      if ((row?.managerEscalation?.status || row.escalationStatus) && escalationAt && isSameLocalDay(escalationAt, now)) {
        events.push({
          id: `escalated-${row.id}`,
          at: escalationAt,
          type: "Manager notified",
          label,
          detail: `${row?.managerEscalation?.managerName || row.managerName || "Management"} • ${store}`,
          tone: "#fb7185",
        });
      }
      const assignedAt = neoTsDate(row.ownerAssignedAt || row.assignedAt || row?.responseDecision?.assignedAt);
      if (assignedAt && isSameLocalDay(assignedAt, now) && (row.ownerSource === "manager_override" || row.ownerSource === "auto_fallback" || row?.responseDecision?.assignedToName)) {
        const owner = row.assignedToName || row?.responseDecision?.assignedToName || "New owner";
        events.push({
          id: `assigned-${row.id}`,
          at: assignedAt,
          type: row.ownerSource === "auto_fallback" ? "Neo reassigned" : "Owner assigned",
          label,
          detail: `${owner} • ${store}`,
          tone: "#60a5fa",
        });
      }
    });
    return events.sort((a,b) => b.at.getTime() - a.at.getTime());
  }, [filteredDutyRows, storeName, now]);

  const responseRate = useMemo(() => buildManagerResponseRate(filteredDutyRows), [filteredDutyRows]);

  const dutyStatsByStaff = useMemo(() => {
    const map = new Map();
    (todayResponseRows || []).forEach((row) => {
      const names = [
        row.completedBy,
        row.assignedToName,
        ...(Array.isArray(row?.dueNotification?.recipientNames) ? row.dueNotification.recipientNames : []),
      ].filter(Boolean);
      const unique = Array.from(new Set(names.map((name) => String(name).trim()).filter(Boolean)));
      unique.forEach((name) => {
        const key = name.toLowerCase();
        const current = map.get(key) || { total: 0, completed: 0, late: 0, escalated: 0 };
        current.total += 1;
        if (row.completed === true || row.status === "completed") {
          current.completed += 1;
          if (row.completionStatus === "late") current.late += 1;
        }
        if (row?.managerEscalation?.status || row.escalationStatus) current.escalated += 1;
        map.set(key, current);
      });
    });
    return map;
  }, [todayResponseRows]);

  const scheduleRows = useMemo(() => {
    const matched = new Set();
    const pickActual = (entry) => {
      const sid=String(entry.staffId||"").trim(), name=String(entry.staffName||"").trim().toLowerCase();
      return (todayAttendanceShifts||[]).filter((a)=>!matched.has(a.id)).filter((a)=>{ const aid=String(a.staffId||"").trim(), an=String(a.staffName||a.name||"").trim().toLowerCase(); return (sid&&aid&&sid===aid)||(name&&an&&name===an); }).sort((a,b)=>(!b.clockOutSec?1:0)-(!a.clockOutSec?1:0)||Number(b.clockInSec||0)-Number(a.clockInSec||0))[0]||null;
    };
    const planned=(todayScheduleEntries||[]).map((entry)=>{ const actual=pickActual(entry); if(actual) matched.add(actual.id); return { ...entry, actual, scheduledText:scheduleWindow(entry).label, intelligence:managerScheduleIntelligence({entry,actual,now,graceMinutes:scheduleGraceMinutes}) }; });
    const unscheduled=(todayAttendanceShifts||[]).filter((a)=>!matched.has(a.id)).map((actual)=>({ id:`unscheduled-${actual.id}`, staffId:actual.staffId||"", staffName:actual.staffName||actual.name||"Staff member", roleName:actual.roleName||actual.role||"", storeId, actual, scheduledText:"Not scheduled", intelligence:actual.scheduleVarianceApproved===true?{label:"Unscheduled work · approved",tone:"#34d399"}:actual.scheduleVarianceRejected===true?{label:"Unscheduled work · rejected",tone:"#fb7185"}:{label:"Clocked in without a published shift · approval required",tone:"#fbbf24"} }));
    return [...planned,...unscheduled];
  }, [todayScheduleEntries, todayAttendanceShifts, now, scheduleGraceMinutes, storeId]);

  const selectedScheduleRow = useMemo(
    () => scheduleRows.find((row) => row.id === selectedScheduleRowId) || null,
    [scheduleRows, selectedScheduleRowId]
  );

  useEffect(() => {
    if (!selectedScheduleRow || !orgId || !storeId) {
      setManagerVarianceEntries([]);
      setManagerVarianceError("");
      setManagerVarianceLoading(false);
      return undefined;
    }
    const staffName = String(selectedScheduleRow.staffName || selectedScheduleRow.actual?.staffName || selectedScheduleRow.actual?.name || "").trim();
    if (!staffName) return undefined;
    let cancelled = false;
    setManagerVarianceLoading(true);
    setManagerVarianceError("");
    (async () => {
      try {
        const monthKey = managerMonthKey(now);
        const scheduleCollection = collection(db, "orgs", orgId, "neo_schedule_entries");
        const toPublishedMonthRows = (snap) => snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => row.published === true && String(row.draftStatus || "") === "published")
          .filter((row) => String(row.storeId || "") === String(storeId))
          .filter((row) => String(row.date || "").startsWith(monthKey));
        const mergeRows = (...groups) => {
          const byId = new Map();
          groups.flat().forEach((row) => { if (row?.id) byId.set(row.id, row); });
          return Array.from(byId.values()).sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
        };
        const hasWorkingShift = (rows) => rows.some((row) => {
          if (row?.isAttendance === true || row?.attendanceTypeId) return false;
          if (row?.off === true || String(row?.publishedShift || "").trim().toUpperCase() === "OFF") return false;
          const win = scheduleWindow(row);
          return Boolean(win.startTime && win.endTime);
        });

        // Keep the normal path cheap: one narrow staff-name read. Legacy staff
        // records can have schedule rows split across old/new identity values,
        // so only fall back to staffId when the name result contains no actual
        // working shift (Melonie exposed this as 0 scheduled + all time as OT).
        const nameSnap = await getDocs(query(
          scheduleCollection,
          where("monthKey", "==", monthKey),
          where("storeId", "==", storeId),
          where("staffName", "==", staffName)
        ));
        if (cancelled) return;
        let rows = toPublishedMonthRows(nameSnap);

        const staffId = String(selectedScheduleRow.staffId || selectedScheduleRow.actual?.staffId || "").trim();
        if (!hasWorkingShift(rows) && staffId) {
          const idSnap = await getDocs(query(
            scheduleCollection,
            where("monthKey", "==", monthKey),
            where("storeId", "==", storeId),
            where("staffId", "==", staffId)
          ));
          if (cancelled) return;
          rows = mergeRows(rows, toPublishedMonthRows(idSnap));
        } else {
          rows = mergeRows(rows);
        }

        // Last-resort legacy recovery. This runs only for a suspicious result,
        // never as the normal drawer path. It lets Neo recover old rows whose
        // staffName/staffId changed while keeping the usual Firestore read narrow.
        if (!hasWorkingShift(rows)) {
          const storeMonthSnap = await getDocs(query(
            scheduleCollection,
            where("monthKey", "==", monthKey),
            where("storeId", "==", storeId)
          ));
          if (cancelled) return;
          const normalized = (value) => String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
          const wantedName = normalized(staffName);
          const recovered = toPublishedMonthRows(storeMonthSnap).filter((row) => {
            const rowId = String(row.staffId || "").trim();
            return (staffId && rowId && rowId === staffId) || (wantedName && normalized(row.staffName) === wantedName);
          });
          rows = mergeRows(rows, recovered);
        }

        setManagerVarianceEntries(rows);
      } catch (error) {
        if (cancelled) return;
        console.error("[Neo Manager] Staff schedule variance load failed", error);
        setManagerVarianceEntries([]);
        setManagerVarianceError("Schedule variance is temporarily unavailable.");
      } finally {
        if (!cancelled) setManagerVarianceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedScheduleRowId, orgId, storeId]);

  useEffect(() => {
    if (!selectedScheduleRow || !orgId || !storeId) {
      setManagerVarianceShifts([]);
      return undefined;
    }

    const staffName = String(selectedScheduleRow.staffName || selectedScheduleRow.actual?.staffName || selectedScheduleRow.actual?.name || "").trim();
    if (!staffName) {
      setManagerVarianceShifts([]);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        // Schedule Intelligence needs MONTH attendance, while the manager's
        // protected live listener above intentionally contains TODAY only.
        // Load this selected staff member's history once, on demand, rather
        // than widening the live Response listener back to store history.
        const [nestedSnap, legacySnap] = await Promise.all([
          getDocs(query(
            shiftsColl(orgId, storeId),
            where("name", "==", staffName)
          )),
          getDocs(query(
            collection(db, "cashup_shifts"),
            where("orgId", "==", orgId),
            where("name", "==", staffName)
          )),
        ]);
        if (cancelled) return;

        const monthKey = managerMonthKey(new Date());
        const normalizedStoreName = String(storeName || "").trim().toLowerCase();
        const isCurrentMonth = (row) => {
          const clockIn = managerTsToDate(row?.clockIn || row?.clockInAt || row?.in || row?.inAt);
          return Boolean(clockIn && managerMonthKey(clockIn) === monthKey);
        };
        const usable = (row) => row?.archived !== true && row?.isArchived !== true && isCurrentMonth(row);

        const nested = nestedSnap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}), __source: "nested" }))
          .filter(usable);
        const legacy = legacySnap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}), __source: "legacy" }))
          .filter(usable)
          .filter((row) => {
            const rowStoreId = String(row.storeId || row.store || "").trim();
            const rowStoreName = String(row.storeName || row.storeLabel || row.store || "").trim().toLowerCase();
            return rowStoreId === String(storeId) || (normalizedStoreName && rowStoreName === normalizedStoreName);
          });

        // Prefer the nested authority when a migrated legacy row represents
        // the same clock-in. This prevents historical attendance being counted twice.
        const seen = new Set();
        const merged = [...nested, ...legacy].filter((row) => {
          const clockIn = managerTsToDate(row?.clockIn || row?.clockInAt || row?.in || row?.inAt);
          const key = `${String(row.staffId || row.name || staffName).trim().toLowerCase()}|${clockIn?.getTime?.() || row.id}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setManagerVarianceShifts(merged);
      } catch (error) {
        if (!cancelled) {
          console.error("[Neo Manager] Staff monthly attendance load failed", error);
          setManagerVarianceShifts([]);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [selectedScheduleRowId, orgId, storeId, storeName]);

  const currentScheduleDates = useMemo(() => {
    const seen = new Set();
    return currentScheduleEntries
      .map((row) => String(row.date || ""))
      .filter((date) => {
        if (!date || seen.has(date)) return false;
        seen.add(date);
        return true;
      });
  }, [currentScheduleEntries]);

  const currentScheduleDayRows = useMemo(
    () =>
      currentScheduleEntries.filter(
        (row) => String(row.date || "") === String(currentScheduleSelectedDate || "")
      ),
    [currentScheduleEntries, currentScheduleSelectedDate]
  );

  useEffect(() => {
    if (!currentScheduleOpen || !currentScheduleDateStripRef.current) return undefined;
    if (!currentScheduleSelectedDate) return undefined;

    const strip = currentScheduleDateStripRef.current;
    const timer = setTimeout(() => {
      const selected = strip.querySelector(
        `[data-neo-schedule-date="${currentScheduleSelectedDate}"]`
      );
      if (!selected) return;

      const stripRect = strip.getBoundingClientRect();
      const selectedRect = selected.getBoundingClientRect();
      const targetLeft =
        strip.scrollLeft +
        (selectedRect.left - stripRect.left) -
        ((strip.clientWidth - selectedRect.width) / 2);

      const maxLeft = Math.max(0, strip.scrollWidth - strip.clientWidth);

      strip.scrollTo({
        left: Math.min(maxLeft, Math.max(0, targetLeft)),
        behavior: "smooth",
      });
    }, 60);

    return () => clearTimeout(timer);
  }, [
    currentScheduleOpen,
    currentScheduleSelectedDate,
    currentScheduleDates,
  ]);

  useEffect(() => {
    if (selectedScheduleRowId && !selectedScheduleRow) {
      setSelectedScheduleRowId("");
    }
  }, [selectedScheduleRowId, selectedScheduleRow]);


  const scheduleDecisionMeta = useCallback((row) => {
    const actual = row?.actual || null;
    const label = String(row?.intelligence?.label || "").toLowerCase();
    const unscheduled =
      String(row?.id || "").startsWith("unscheduled-") ||
      label.includes("unscheduled") ||
      label.includes("off day");
    const overtime = label.includes("overtime");
    const actionable = Boolean(actual?.clockInSec && actual?.clockOutSec && (unscheduled || overtime));

    return {
      actionable,
      unscheduled,
      overtime,
      varianceType: unscheduled ? "unscheduled" : overtime ? "overtime" : "actual_shift",
      decisionAvailableAfterClockOut: Boolean(
        actual?.clockInSec &&
        !actual?.clockOutSec &&
        (unscheduled || overtime)
      ),
    };
  }, []);

  const loadScheduleDecisionHistory = useCallback(async () => {
    const sessionToken = String(managerActor.sessionToken || "");

    if (!orgId || !storeId || !sessionToken) {
      setScheduleHistoryRows([]);
      return [];
    }

    setScheduleHistoryLoading(true);

    try {
      const functions = getFunctions(app, "us-central1");
      const loadHistory = httpsCallable(functions, "getNeoManagerScheduleHistory");
      const result = await loadHistory({
        sessionToken,
        orgId,
        storeId,
        date: managerTodayKey(new Date()),
      });

      const rows = Array.isArray(result?.data?.rows) ? result.data.rows : [];
      setScheduleHistoryRows(rows);
      return rows;
    } catch (error) {
      console.error("[Neo MS4] Could not load shared schedule history", error);
      const code = String(error?.code || "");
      showToast(
        code === "functions/unauthenticated"
          ? "Your manager session has expired. Please log in again."
          : "Could not load schedule decision history.",
        "error"
      );
      setScheduleHistoryRows([]);
      return [];
    } finally {
      setScheduleHistoryLoading(false);
    }
  }, [
    orgId,
    storeId,
    managerActor.sessionToken,
    showToast,
  ]);

  useEffect(() => {
    if (!scheduleHistoryOpen) return undefined;

    loadScheduleDecisionHistory();
    const timer = setInterval(() => {
      loadScheduleDecisionHistory();
    }, 20000);

    return () => clearInterval(timer);
  }, [scheduleHistoryOpen, loadScheduleDecisionHistory]);

  const decideScheduleVariance = useCallback(async (row, approved) => {
    const actual = row?.actual || null;
    const meta = scheduleDecisionMeta(row);

    if (!orgId || !actual?.clockInSec || !actual?.clockOutSec || !meta.actionable) {
      return;
    }

    const staffId = String(row.staffId || actual.staffId || "").trim();
    const staffName = String(
      row.staffName || actual.staffName || actual.name || "Staff member"
    ).trim();
    const finalStoreId = String(row.storeId || actual.storeId || storeId || "").trim();
    const dateKey = String(
      row.date || managerTodayKey(new Date(actual.clockInSec * 1000))
    );
    const scheduleEntryId =
      !String(row.id || "").startsWith("unscheduled-") && row.id
        ? String(row.id)
        : `${finalStoreId}__${staffId}__${dateKey}`;

    if (!finalStoreId || !staffId || !scheduleEntryId || !actual?.id) {
      showToast("Could not resolve this staff schedule identity.", "error");
      return;
    }

    const sessionToken = String(managerActor.sessionToken || "");
    if (!sessionToken) {
      showToast(
        "Your secure manager session is missing. Please return to Manager Login and sign in again.",
        "error"
      );
      return;
    }

    const decisionKey = String(row.id || scheduleEntryId);
    setScheduleDecisionState((current) => ({
      ...current,
      [decisionKey]: "saving",
    }));

    try {
      const functions = getFunctions(app, "us-central1");
      const decide = httpsCallable(functions, "decideNeoScheduleException");

      const result = await decide({
        sessionToken,
        orgId,
        storeId: finalStoreId,
        scheduleEntryId,
        shiftId: String(actual.id || "").replace(/^nested:/, ""),
        staffId,
        staffName,
        date: dateKey,
        decision: approved ? "approve" : "reject",
        varianceType: meta.varianceType,
        intelligenceLabel: String(row?.intelligence?.label || ""),
      });

      const data = result?.data || {};
      if (data?.ok !== true) {
        throw new Error("Schedule decision was not confirmed.");
      }

      setScheduleDecisionState((current) => ({
        ...current,
        [decisionKey]: approved ? "approved" : "rejected",
      }));

      showToast(
        approved
          ? `${staffName}'s schedule exception was approved.`
          : `${staffName}'s schedule exception was rejected.`,
        "success"
      );

      if (scheduleHistoryOpen) {
        loadScheduleDecisionHistory();
      }
    } catch (error) {
      console.error("[Neo MS3.1] Could not save schedule exception decision", error);

      setScheduleDecisionState((current) => ({
        ...current,
        [decisionKey]: "error",
      }));

      const code = String(error?.code || "");
      const message =
        code === "functions/unauthenticated"
          ? "Your manager session has expired. Please return to Manager Login."
          : code === "functions/permission-denied"
          ? "This manager is not authorised to make this schedule decision."
          : code === "functions/not-found"
          ? "The schedule or shift record could not be found."
          : "Could not save the schedule decision.";

      showToast(message, "error");
    }
  }, [
    orgId,
    storeId,
    managerActor.sessionToken,
    scheduleDecisionMeta,
    showToast,
    scheduleHistoryOpen,
    loadScheduleDecisionHistory,
  ]);


  const responseFilterCounts = useMemo(() => {
    const counts = {
      all: 0,
      opening: 0,
      closing: 0,
      handover: 0,
      security: 0,
      cleaning: 0,
      cashup: 0,
      custom: 0,
    };

    (todayResponseRows || []).forEach((row) => {
      counts.all += 1;
      const category = normalizeDutyCategory(row);
      counts[category] = (counts[category] || 0) + 1;
    });

    return counts;
  }, [todayResponseRows]);



  useEffect(() => {
    if (responseFilter === "all") return;
    if (!responseFilterCounts[responseFilter]) setResponseFilter("all");
  }, [responseFilter, responseFilterCounts]);

  const executiveSignal = useMemo(
    () => summarizeExecutiveSignal({
      attention: attentionDuties,
      filteredRows: filteredDutyRows,
      now,
    }),
    [attentionDuties, filteredDutyRows, now]
  );

  const categoryExceptionSummary = useMemo(() => {
    const labels = {
      opening: "Opening",
      closing: "Closing",
      handover: "Handover",
      security: "Security",
      cleaning: "Cleaning",
      cashup: "Cashup",
      custom: "Custom",
    };

    return Object.entries(responseFilterCounts)
      .filter(([key]) => key !== "all")
      .map(([key, count]) => ({ key, label: labels[key] || key, count: Number(count || 0) }))
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [responseFilterCounts]);

  const weakestDutyCategory = useMemo(() => {
    const byCategory = new Map();

    (filteredDutyRows || []).forEach((row) => {
      const key = normalizeDutyCategory(row);
      const current = byCategory.get(key) || { key, total: 0, completed: 0, late: 0, open: 0 };
      current.total += 1;

      if (row.completed === true || row.status === "completed") {
        current.completed += 1;
        if (row.completionStatus === "late") current.late += 1;
      } else {
        const state = dutyManagerState(row, now);
        if (["due", "overdue", "escalated"].includes(state)) current.open += 1;
      }

      byCategory.set(key, current);
    });

    return Array.from(byCategory.values())
      .map((item) => ({
        ...item,
        completionRate: item.total ? Math.round((item.completed / item.total) * 100) : 0,
        riskScore: item.open * 2 + item.late + Math.max(0, item.total - item.completed),
      }))
      .sort((a, b) => b.riskScore - a.riskScore || a.completionRate - b.completionRate)[0] || null;
  }, [filteredDutyRows, now]);

  const areaIntelligence = useMemo(() => {
    const labels = { opening: "Opening", closing: "Closing", handover: "Handover", security: "Security", cleaning: "Cleaning", cashup: "Cashup", custom: "Custom" };
    const map = new Map(Object.keys(labels).map((key) => [key, { key, label: labels[key], total: 0, completed: 0, live: 0, decisions: 0, watching: 0 }]));

    (todayResponseRows || []).forEach((row) => {
      const key = normalizeDutyCategory(row);
      const current = map.get(key) || { key, label: labels[key] || "Custom", total: 0, completed: 0, live: 0, decisions: 0, watching: 0 };
      const classification = classifyResponseException(row, now);
      if (classification.operationalAge === "stale") return;
      current.total += 1;
      if (row.completed === true || row.status === "completed") current.completed += 1;
      const state = dutyManagerState(row, now);
      if (["due", "overdue", "escalated"].includes(state)) current.live += 1;
      if (classification.responseState === "needs_decision") current.decisions += 1;
      if (classification.responseState === "watching" && ["due", "overdue", "escalated"].includes(state)) current.watching += 1;
      map.set(key, current);
    });

    return Array.from(map.values()).map((item) => {
      const responseRate = item.total ? Math.round((item.completed / item.total) * 100) : null;
      let health = "No activity";
      let tone = "#94a3b8";
      if (item.decisions > 0 || item.live >= 3) { health = "At risk"; tone = "#fb7185"; }
      else if (item.watching > 0 || item.live > 0) { health = "Attention"; tone = "#fbbf24"; }
      else if (item.total > 0) { health = "Healthy"; tone = "#34d399"; }
      return { ...item, responseRate, health, tone };
    }).filter((item) => item.total > 0 || item.live > 0);
  }, [todayResponseRows, now]);

  const staffPerformance = useMemo(() => {
    const map = new Map();

    (todayResponseRows || []).forEach((row) => {
      const classification = classifyResponseException(row, now);
      if (classification.operationalAge === "stale") return;

      const name = String(
        row.completedBy ||
        row.assignedToName ||
        row?.dueNotification?.recipientNames?.[0] ||
        ""
      ).trim();
      if (!name) return;

      const current = map.get(name) || { name, total: 0, completed: 0 };
      current.total += 1;
      if (row.completed === true || row.status === "completed") {
        current.completed += 1;
      }
      map.set(name, current);
    });

    return Array.from(map.values())
      .map((person) => ({
        ...person,
        responseRate: person.total
          ? Math.round((person.completed / person.total) * 100)
          : 0,
      }))
      .sort((a, b) => b.responseRate - a.responseRate || b.completed - a.completed);
  }, [todayResponseRows, now]);

  const peopleIntelligence = useMemo(() => {
    const map = new Map();
    (todayResponseRows || []).forEach((row) => {
      const classification = classifyResponseException(row, now);
      if (classification.operationalAge === "stale") return;
      const name = String(row.completedBy || row.assignedToName || row.dueNotification?.recipientNames?.[0] || "").trim();
      if (!name) return;
      const current = map.get(name) || { name, total: 0, completed: 0, late: 0, escalated: 0, decisions: 0, watching: 0 };
      current.total += 1;
      if (row.completed === true || row.status === "completed") {
        current.completed += 1;
        if (row.completionStatus === "late") current.late += 1;
      }
      if (row?.managerEscalation?.status || row.escalationStatus) current.escalated += 1;
      if (classification.responseState === "needs_decision") current.decisions += 1;
      if (classification.responseState === "watching" && ["due", "overdue", "escalated"].includes(dutyManagerState(row, now))) current.watching += 1;
      map.set(name, current);
    });
    return Array.from(map.values()).map((person) => ({
      ...person,
      responseRate: person.total ? Math.round((person.completed / person.total) * 100) : 0,
      concernScore: person.decisions * 5 + person.escalated * 3 + person.watching * 2 + person.late,
    })).filter((person) => person.concernScore > 0 || person.responseRate < 80)
      .sort((a, b) => b.concernScore - a.concernScore || a.responseRate - b.responseRate)
      .slice(0, 5);
  }, [todayResponseRows, now]);

  const openScheduleIssueCount = useMemo(
    () => scheduleIssues.filter((issue) => !["resolved", "closed"].includes(String(issue.status || "open").toLowerCase())).length,
    [scheduleIssues]
  );

  const pendingLeaveRequests = useMemo(
    () => leaveRequests.filter((row) => String(row.status || "pending").toLowerCase() === "pending"),
    [leaveRequests]
  );
  const resolvedLeaveRequests = useMemo(
    () => leaveRequests.filter((row) => ["approved", "rejected"].includes(String(row.status || "").toLowerCase())),
    [leaveRequests]
  );
  const selectedLeaveRequest = useMemo(
    () => leaveRequests.find((row) => row.id === selectedLeaveId) || null,
    [leaveRequests, selectedLeaveId]
  );

  return (
    <div className="page" style={styles.page}>
      <div style={styles.surface}>
        <div style={{ ...styles.hero, padding: isMobile ? "16px 14px" : isCompact ? "20px 18px" : styles.hero.padding, borderRadius: isMobile ? 18 : isCompact ? 22 : styles.hero.borderRadius }}>
          <div style={styles.heroGlow} />

          <div style={styles.badgeRow}>
            <div style={styles.badge}>Neo Manager Dashboard · Build {NEO_BUILD_ID}</div>
            <button type="button" onClick={toggleTheme} style={styles.themeButton}>
              {themeMode === "dark" ? "☀ Light mode" : "🌙 Dark mode"}
            </button>
          </div>

          <div style={styles.topRow}>
            <div style={styles.heroLeft}>
              <h1 style={{ ...styles.title, fontSize: isMobile ? "1.9rem" : styles.title.fontSize }}>
                Manager Dashboard
                <span style={{ ...styles.titleAccent, display: isMobile ? "block" : "inline", marginLeft: isMobile ? 0 : styles.titleAccent.marginLeft, marginTop: isMobile ? 5 : 0 }}>{storeName}</span>
              </h1>
              {!isMobile ? (
                <div style={styles.subtitle}>
                  Live store view for managers, active staff, and quick access to reports and settings.
                </div>
              ) : null}

              <div style={{ ...styles.metaPills, marginTop: isMobile ? 12 : styles.metaPills.marginTop }}>
                <div style={{ ...styles.metaPill, padding: isMobile ? "8px 10px" : styles.metaPill.padding }}>Welcome {managerName}</div>
                {!isMobile ? <div style={styles.metaPill}>Manager session live</div> : null}
                <div style={{ ...styles.metaPill, padding: isMobile ? "8px 10px" : styles.metaPill.padding }}>{onlineCount} active now</div>
              </div>
            </div>

            <div style={{ ...styles.heroRight, alignItems: isMobile ? "flex-start" : styles.heroRight.alignItems, minWidth: isMobile ? 0 : styles.heroRight.minWidth, marginLeft: isMobile ? 0 : styles.heroRight.marginLeft, width: isMobile ? "100%" : "auto", gap: isMobile ? 5 : styles.heroRight.gap }}>
              <div style={{ ...styles.liveTime, fontSize: isMobile ? "1.65rem" : styles.liveTime.fontSize }}>{timeText}</div>
              <div style={styles.liveSub}>{dateText}</div>
              <div style={{ ...styles.onlinePill, padding: isMobile ? "6px 10px" : styles.onlinePill.padding }}>Online</div>
            </div>
          </div>
        </div>

        <div style={{
          display: "flex",
          gap: 8,
          flexWrap: "nowrap",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          padding: 8,
          borderRadius: 999,
          width: isMobile ? "100%" : "fit-content",
          maxWidth: "100%",
          background: themeMode === "dark" ? "rgba(2,6,23,.46)" : "rgba(230,239,251,.75)",
          border: themeMode === "dark" ? "1px solid rgba(123,186,255,.16)" : "1px solid rgba(150,174,205,.24)",
        }}>
          {[
            ["response", "Response"],
            ["active", "Active Staff"],
            ["schedule", openScheduleIssueCount ? `Schedule (${openScheduleIssueCount})` : "Schedule"],
            ["leave", pendingLeaveRequests.length ? `Leave (${pendingLeaveRequests.length})` : "Leave"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setManagerTab(key)}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 0 20px rgba(79,156,255,.18)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "none";
              }}
              style={{
                borderRadius: 999,
                padding: "10px 15px",
                border: managerTab === key ? "1px solid rgba(79,156,255,.46)" : "1px solid transparent",
                background: managerTab === key
                  ? "linear-gradient(135deg, rgba(37,99,235,.92), rgba(56,189,248,.92))"
                  : "transparent",
                color: managerTab === key ? "#fff" : (themeMode === "dark" ? "rgba(223,233,248,.75)" : "#395d86"),
                fontWeight: 900,
                whiteSpace: "nowrap",
                flex: "0 0 auto",
                cursor: "pointer",
                transition: "all .18s ease",
              }}
            >
              {label}
            </button>
          ))}
        </div>

{managerTab === "active" ? (<>
        <div style={{ ...styles.statsGrid, gridTemplateColumns: isCompact ? "1fr" : styles.statsGrid.gridTemplateColumns }}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Active Staff</div>
            <div style={styles.statValue}>{onlineCount}</div>
            <div style={styles.statSub}>Currently clocked in at this store</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statLabel}>Store</div>
            <div style={styles.statValue} style={{ ...styles.statValue, fontSize: 26 }}>
              {storeName}
            </div>
            <div style={styles.statSub}>Bound device workspace</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statLabel}>Manager Session</div>
            <div style={styles.statValue} style={{ ...styles.statValue, fontSize: 26 }}>
              Live
            </div>
            <div style={styles.statSub}>Authenticated and protected</div>
          </div>
        </div>

        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Active Staff</h2>
              <div style={styles.panelSub}>Live open shifts and current durations</div>
            </div>
            <div style={styles.panelBadge}>🟢 Live now</div>
          </div>

          {activeShifts.length === 0 ? (
            <div style={styles.emptyWrap}>
              <div style={styles.emptyTitle}>No one is clocked in</div>
              <div>The active shift list will appear here as staff clock in.</div>
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Role</th>
                    <th style={styles.th}>Clocked In</th>
                    <th style={styles.th}>Duration</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {activeShifts.map((s) => {
                    const initials = String(s.name || "?")
                      .split(" ")
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((p) => p[0]?.toUpperCase())
                      .join("");
                    const staffDuty = dutyStatsByStaff.get(String(s.name || "").trim().toLowerCase()) || {
                      total: 0, completed: 0, late: 0, escalated: 0
                    };
                    const isExpanded = expandedStaffId === s.id;

                    return (
                      <React.Fragment key={s.id}>
                      <tr
                          onClick={() => setExpandedStaffId(isExpanded ? "" : s.id)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.filter = "brightness(1.08)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.filter = "none";
                          }}
                          style={{ cursor: "pointer" }}
                        >
                        <td style={{ ...styles.td, ...styles.rowFirst, ...styles.row }}>
                          <div style={styles.nameCell}>
                            <div style={styles.avatar}>{initials || "?"}</div>
                            <div>
                              <div style={styles.nameText}>{s.name || "—"}</div>
                              <div style={styles.mutedMeta}>
                                {s.breakStatus === "on_break"
                                  ? `Currently on break • ${getBreakMinutes(s.breakStartedAt)} min`
                                  : Number(s.lastBreakMinutes || 0) > 0
                                  ? `Break taken • ${Number(s.lastBreakMinutes)} min`
                                  : "Open shift"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ ...styles.td, ...styles.row }}>
                          <span style={styles.rolePill}>{s.role || "—"}</span>
                        </td>
                        <td style={{ ...styles.td, ...styles.row }}>
                          {formatClockTime(s.clockInDate)}
                        </td>
                        <td style={{ ...styles.td, ...styles.row }}>{s.duration || "—"}</td>
                        <td style={{ ...styles.td, ...styles.rowLast, ...styles.row }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <span style={s.breakStatus === "on_break" ? styles.statusPillBreak : styles.statusPill}>
                              {s.breakStatus === "on_break"
                                ? `On break${s.breakStartedAt ? ` • ${getBreakMinutes(s.breakStartedAt)} min` : ""}`
                                : "On shift"}
                            </span>

                            {s.breakStatus !== "on_break" && Number(s.lastBreakMinutes || 0) > 0 && (
                              <div style={styles.rowMetaSoft}>Break taken • {Number(s.lastBreakMinutes)} min</div>
                            )}

                            {s.lunchTaken === false && (
                              <div style={styles.rowMetaWarn}>Lunch not taken</div>
                            )}

                            {s.lunchTaken === false && !!String(s.lunchReason || "").trim() && (
                              <div style={styles.rowMetaSoft}>Reason: {String(s.lunchReason || "").trim()}</div>
                            )}
                          </div>
                        </td>
                      </tr>
                        {isExpanded ? (
                          <tr>
                            <td colSpan={5} style={{
                              padding: "0 14px 16px",
                              background: themeMode === "dark" ? "rgba(8,25,48,.72)" : "rgba(245,249,255,.92)",
                              borderBottom: themeMode === "dark" ? "1px solid rgba(123,186,255,.12)" : "1px solid rgba(150,174,205,.18)",
                            }}>
                              <div style={{
                                display: "grid",
                                gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0,1fr))",
                                gap: 10,
                                padding: 12,
                                borderRadius: 14,
                                border: themeMode === "dark" ? "1px solid rgba(79,156,255,.16)" : "1px solid rgba(79,156,255,.18)",
                              }}>
                                <div><strong>{staffDuty.completed}/{staffDuty.total}</strong><div style={styles.mutedMeta}>Duties completed</div></div>
                                <div><strong>{staffDuty.late}</strong><div style={styles.mutedMeta}>Completed late</div></div>
                                <div><strong>{staffDuty.escalated}</strong><div style={styles.mutedMeta}>Escalations</div></div>
                                <div><strong>{s.duration || "—"}</strong><div style={styles.mutedMeta}>Current shift</div></div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>


        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Analytics</h2>
              <div style={styles.panelSub}>Operational trends and response insights.</div>
            </div>
            <div style={styles.panelBadge}>Executive</div>
          </div>

          <div style={styles.responseGrid}>
            <div style={styles.responseCard}>
              <div style={styles.responseMetricLabel}>Average Response</div>
              <div style={styles.responseMetricValue}>--</div>
              <div style={styles.statSub}>Average completion time</div>
            </div>
            <div style={styles.responseCard}>
              <div style={styles.responseMetricLabel}>Recurring Duties</div>
              <div style={styles.responseMetricValue}>{attentionDuties.length}</div>
              <div style={styles.statSub}>Currently requiring attention</div>
            </div>
            <div style={styles.responseCard}>
              <div style={styles.responseMetricLabel}>Top Performer</div>
              <div style={{ ...styles.responseMetricValue, fontSize: 22 }}>
                {staffPerformance[0]?.name || "—"}
              </div>
              <div style={styles.statSub}>
                {staffPerformance[0]?.responseRate ?? 0}% response rate
              </div>
            </div>
            <div style={styles.responseCard}>
              <div style={styles.responseMetricLabel}>Escalation Trend</div>
              <div style={styles.responseMetricValue}>{escalationHistory.length}</div>
              <div style={styles.statSub}>Recent management interventions</div>
            </div>
          </div>
        </div>


</>) : null}
{managerTab === "leave" ? (<>
        <div style={styles.panel}>
          <div style={{ ...styles.panelHeader, padding: isMobile ? 14 : 18, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div>
              <div style={{ color: themeMode === "dark" ? "#c4b5fd" : "#6d28d9", fontSize: 11, fontWeight: 950, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 6 }}>Leave Intelligence</div>
              <h2 style={{ ...styles.panelTitle, fontSize: isMobile ? 24 : 30 }}>Leave approvals</h2>
              <div style={styles.panelSub}>Review requests, notes and supporting evidence before deciding.</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" onClick={loadLeaveRequests} disabled={leaveLoading} style={{ border: "1px solid rgba(148,163,184,.22)", background: "transparent", color: "inherit", borderRadius: 999, padding: "8px 11px", fontSize: 11, fontWeight: 900, cursor: "pointer" }}>{leaveLoading ? "Loading…" : "Refresh"}</button>
            <div style={{
              ...styles.panelBadge,
              color: pendingLeaveRequests.length ? (themeMode === "dark" ? "#fbbf24" : "#a16207") : (themeMode === "dark" ? "#34d399" : "#047857"),
              background: pendingLeaveRequests.length
                ? (themeMode === "dark" ? "rgba(245,185,66,.12)" : "rgba(183,121,31,.10)")
                : (themeMode === "dark" ? "rgba(61,220,151,.10)" : "rgba(31,157,85,.10)"),
            }}>
              {pendingLeaveRequests.length ? `${pendingLeaveRequests.length} pending` : "No leave waiting"}
            </div>
            </div>
          </div>

          <div style={{ padding: isMobile ? 12 : 18 }}>
            {leaveLoading ? (
              <div style={styles.emptyWrap}><div style={styles.emptyTitle}>Loading leave requests…</div></div>
            ) : pendingLeaveRequests.length === 0 ? (
              <div style={styles.emptyWrap}>
                <div style={styles.emptyTitle}>No leave decisions waiting</div>
                <div>New staff leave requests will appear here.</div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0,1fr))", gap: 12 }}>
                {pendingLeaveRequests.map((request) => (
                  <button
                    key={request.id}
                    type="button"
                    onClick={() => { setSelectedLeaveId(request.id); setLeaveDecisionNote(request.managerNote || ""); }}
                    style={{
                      textAlign: "left",
                      borderRadius: 18,
                      border: themeMode === "dark" ? "1px solid rgba(167,139,250,.24)" : "1px solid rgba(109,40,217,.16)",
                      background: themeMode === "dark" ? "rgba(15,27,48,.92)" : "rgba(255,255,255,.94)",
                      color: themeMode === "dark" ? "#f8fafc" : "#0f2747",
                      padding: 16,
                      cursor: "pointer",
                      boxShadow: themeMode === "dark" ? "0 10px 24px rgba(0,0,0,.18)" : "0 8px 22px rgba(24,55,96,.06)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 950, fontSize: 18 }}>{request.staffName || "Staff member"}</div>
                        <div style={{ marginTop: 3, color: themeMode === "dark" ? "#a8b9cd" : "#60758f", fontSize: 12 }}>{request.staffRole || "Staff"} • {storeName}</div>
                      </div>
                      <span style={{ borderRadius: 999, padding: "6px 9px", fontSize: 11, fontWeight: 900, color: themeMode === "dark" ? "#fbbf24" : "#a16207", background: themeMode === "dark" ? "rgba(245,185,66,.12)" : "rgba(183,121,31,.10)" }}>Needs decision</span>
                    </div>
                    <div style={{ marginTop: 14, fontWeight: 900, color: themeMode === "dark" ? "#c4b5fd" : "#6d28d9" }}>{leaveTypeLabel(request.leaveType)}</div>
                    <div style={{ marginTop: 5, fontSize: 13, color: themeMode === "dark" ? "#cbd5e1" : "#475569" }}>{request.startDate || "—"}{request.endDate && request.endDate !== request.startDate ? ` → ${request.endDate}` : ""}</div>
                    {request.reason ? <div style={{ marginTop: 9, fontSize: 13, lineHeight: 1.5, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>{request.reason}</div> : null}
                    <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {request.evidence?.url ? <span style={{ fontSize: 11, fontWeight: 900, color: themeMode === "dark" ? "#60a5fa" : "#2563eb" }}>📎 Evidence attached</span> : null}
                      <span style={{ fontSize: 11, fontWeight: 900, color: themeMode === "dark" ? "#c4b5fd" : "#6d28d9" }}>Review request →</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {resolvedLeaveRequests.length ? (
          <div style={styles.panel}>
            <div style={{ ...styles.panelHeader, padding: isMobile ? 14 : 18 }}>
              <div>
                <h2 style={{ ...styles.panelTitle, fontSize: 22 }}>Recent leave decisions</h2>
                <div style={styles.panelSub}>Latest approved and rejected requests for this store.</div>
              </div>
            </div>
            <div style={{ padding: isMobile ? 12 : 18, display: "grid", gap: 9 }}>
              {resolvedLeaveRequests.slice(0, 6).map((request) => {
                const approved = String(request.status).toLowerCase() === "approved";
                return (
                  <button
                    key={request.id}
                    type="button"
                    onClick={() => { setSelectedLeaveId(request.id); setLeaveDecisionNote(request.managerNote || ""); }}
                    style={{
                      width: "100%",
                      borderRadius: 14,
                      padding: 13,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      textAlign: "left",
                      cursor: "pointer",
                      color: "inherit",
                      font: "inherit",
                      border: themeMode === "dark" ? "1px solid rgba(148,163,184,.16)" : "1px solid rgba(15,23,42,.08)",
                      background: themeMode === "dark" ? "rgba(15,27,48,.68)" : "rgba(248,250,252,.92)",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 900 }}>{request.staffName || "Staff member"} · {leaveTypeLabel(request.leaveType)}</div>
                      <div style={{ marginTop: 3, fontSize: 12, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>{request.startDate || "—"}{request.endDate && request.endDate !== request.startDate ? ` → ${request.endDate}` : ""}</div>
                      <div style={{ marginTop: 6, fontSize: 11, fontWeight: 900, color: themeMode === "dark" ? "#c4b5fd" : "#6d28d9" }}>View decision details →</div>
                    </div>
                    <span style={{ borderRadius: 999, padding: "6px 9px", fontWeight: 900, fontSize: 11, color: approved ? (themeMode === "dark" ? "#34d399" : "#047857") : (themeMode === "dark" ? "#fb7185" : "#be123c"), background: approved ? "rgba(16,185,129,.10)" : "rgba(244,63,94,.10)" }}>{approved ? "Approved" : "Rejected"}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
</>) : null}

{managerTab === "response" ? (<>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: isCompact ? "nowrap" : "wrap",
          overflowX: isCompact ? "auto" : "visible",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          padding: isMobile ? 8 : 10,
          borderRadius: 18,
          background: themeMode === "dark" ? "rgba(2,6,23,.42)" : "rgba(239,244,251,.86)",
          border: themeMode === "dark" ? "1px solid rgba(123,186,255,.16)" : "1px solid rgba(150,174,205,.24)",
        }}>
          {[
            ["all", "All"],
            ...Object.entries(responseFilterCounts)
              .filter(([key, count]) => key !== "all" && Number(count || 0) > 0)
              .map(([key]) => [key, responseFilterLabel(key)]),
          ].map(([key, label]) => {
            const active = responseFilter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setResponseFilter(key)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 0 18px rgba(79,156,255,.18)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                style={{
                  borderRadius: 999,
                  padding: "8px 11px",
                  border: active ? "1px solid rgba(79,156,255,.46)" : "1px solid rgba(148,163,184,.18)",
                  background: active
                    ? "linear-gradient(135deg, rgba(37,99,235,.90), rgba(56,189,248,.90))"
                    : (themeMode === "dark" ? "rgba(15,23,42,.58)" : "rgba(255,255,255,.76)"),
                  color: active ? "#fff" : (themeMode === "dark" ? "rgba(223,233,248,.74)" : "#395d86"),
                  fontWeight: 900,
                  fontSize: 11,
                  whiteSpace: "nowrap",
                  flex: "0 0 auto",
                  cursor: "pointer",
                  transition: "all .18s ease",
                }}
              >
                {label} <span style={{ opacity: .74 }}>({responseFilterCounts[key] || 0})</span>
              </button>
            );
          })}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.55fr) minmax(300px, .85fr)", gap: 16 }}>
          <div style={{
            borderRadius: 24,
            padding: isCompact ? 16 : 20,
            background: themeMode === "dark"
              ? "radial-gradient(circle at top left, rgba(45,212,191,.10), transparent 34%), linear-gradient(135deg, rgba(5,18,38,.96), rgba(14,36,66,.96))"
              : "radial-gradient(circle at top left, rgba(45,212,191,.08), transparent 34%), linear-gradient(135deg, rgba(255,255,255,.98), rgba(244,248,255,.98))",
            border: themeMode === "dark" ? "1px solid rgba(45,212,191,.18)" : "1px solid rgba(45,212,191,.20)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div>
                <div style={{ color: "#2dd4bf", fontSize: 11, fontWeight: 950, letterSpacing: ".14em", textTransform: "uppercase" }}>Neo Brief</div>
                <h2 style={{ ...styles.panelTitle, marginTop: 5, fontSize: isMobile ? 24 : styles.panelTitle.fontSize }}>
                  {responseBuckets.needsDecision.length
                    ? `${responseBuckets.needsDecision.length} ${responseBuckets.needsDecision.length === 1 ? "situation needs" : "situations need"} your decision`
                    : responseBuckets.watching.length
                      ? `Neo is handling ${responseBuckets.watching.length} active ${responseBuckets.watching.length === 1 ? "response" : "responses"}`
                      : "Operation is clear"}
                </h2>
              </div>
              <div style={{ ...styles.panelBadge, color: responseBuckets.needsDecision.length ? (themeMode === "dark" ? "#fb7185" : "#be123c") : (themeMode === "dark" ? "#34d399" : "#047857") }}>
                {responseBuckets.needsDecision.length ? "Action required" : "No decision waiting"}
              </div>
            </div>

            <div style={{ marginTop: 14, fontSize: isCompact ? 13 : 14, lineHeight: 1.65, color: themeMode === "dark" ? "#cbd5e1" : "#475569" }}>
              {responseBuckets.needsDecision.length ? (
                <>
                  <strong style={{ color: themeMode === "dark" ? "#f8fafc" : "#0f172a" }}>
                    {responseBuckets.needsDecision[0]?.dutyLabel || responseBuckets.needsDecision[0]?.label || "A duty"}
                  </strong>{" "}
                  needs management input now.
                  {responseBuckets.needsDecision.length > 1 ? ` ${responseBuckets.needsDecision.length - 1} more ${responseBuckets.needsDecision.length - 1 === 1 ? "decision is" : "decisions are"} waiting in the Response Desk.` : ""}
                </>
              ) : responseBuckets.watching.length ? (
                <>No manager decision is currently blocked. Neo is actively chasing {responseBuckets.watching.length} {responseBuckets.watching.length === 1 ? "duty" : "duties"} and will surface a decision only if intervention becomes necessary.</>
              ) : (
                <>No live exception currently requires management intervention. Historical/stale duties remain outside the live response view.</>
              )}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
              <span style={{ ...styles.responseChip, color: responseBuckets.needsDecision.length ? (themeMode === "dark" ? "#fb7185" : "#be123c") : (themeMode === "dark" ? "#94a3b8" : "#475569") }}>{responseBuckets.needsDecision.length} need decision</span>
              <span style={{ ...styles.responseChip, color: themeMode === "dark" ? "#fbbf24" : "#a16207" }}>{responseBuckets.watching.length} watching</span>
              <span style={{ ...styles.responseChip, color: themeMode === "dark" ? "#34d399" : "#047857" }}>{responseBuckets.resolved.length} resolved</span>
              {responseBuckets.carryOver.length ? <span style={{ ...styles.responseChip, color: themeMode === "dark" ? "#f472b6" : "#be185d" }}>{responseBuckets.carryOver.length} carry-over</span> : null}
            </div>
          </div>

          <div style={styles.panel}>
            <div style={{ ...styles.panelHeader, paddingBottom: 12 }}>
              <div>
                <div style={{ color: themeMode === "dark" ? "#60a5fa" : "#2563eb", fontSize: 11, fontWeight: 950, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 5 }}>Live Operation</div>
                <h2 style={{ ...styles.panelTitle, fontSize: 22 }}>Right now</h2>
              </div>
              <div style={styles.panelBadge}>● Live</div>
            </div>
            <div style={{ padding: isCompact ? 12 : 16, display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 10 }}>
              {[
                ["Staff", `${onlineCount} active`, themeMode === "dark" ? "#60a5fa" : "#1d4ed8"],
                ["Duties", `${dutyGroups.completed.length}/${todayResponseRows.length}`, themeMode === "dark" ? "#34d399" : "#047857"],
                ["Escalated", dutyGroups.escalated.length, dutyGroups.escalated.length ? (themeMode === "dark" ? "#fb7185" : "#be123c") : (themeMode === "dark" ? "#94a3b8" : "#475569")],
                ["Unowned", (filteredDutyRows || []).filter((row) => ["due","overdue","escalated"].includes(dutyManagerState(row, now)) && !row.assignedToName && !row?.dueNotification?.recipientNames?.[0]).length, themeMode === "dark" ? "#fbbf24" : "#a16207"],
              ].map(([label, value, tone]) => (
                <div key={label} style={{ ...styles.responseCard, padding: 12 }}>
                  <div style={styles.responseMetricLabel}>{label}</div>
                  <div style={{ ...styles.responseMetricValue, fontSize: 22, color: tone }}>{value}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: isCompact ? "0 12px 12px" : "0 16px 16px", fontSize: 11, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>
              {(() => {
                const next = [...dutyGroups.upcoming].sort((a,b) => (neoTsDate(a.dueAt)?.getTime?.() || Infinity) - (neoTsDate(b.dueAt)?.getTime?.() || Infinity))[0];
                if (!next) return "No upcoming duty currently queued.";
                const due = neoTsDate(next.dueAt);
                return `Next: ${next.dutyLabel || next.label || "Duty"}${due ? ` • ${due.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}`;
              })()}
            </div>
          </div>
        </div>

        {dutyLoading && dutyRows.length === 0 ? (
          <div style={{ padding: 14, borderRadius: 16, border: "1px solid rgba(79,156,255,.18)", background: "rgba(79,156,255,.06)", color: themeMode === "dark" ? "#93c5fd" : "#2563eb", fontSize: 12, fontWeight: 800 }}>
            Building manager response view…
          </div>
        ) : null}

        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <div style={{
                color: themeMode === "dark" ? "#60a5fa" : "#2563eb",
                fontSize: 11,
                fontWeight: 950,
                letterSpacing: ".14em",
                textTransform: "uppercase",
                marginBottom: 5,
              }}>
                Manager action queue
              </div>
              <h2 style={{ ...styles.panelTitle, fontSize: isMobile ? 24 : styles.panelTitle.fontSize }}>Response Desk</h2>
              <div style={styles.panelSub}>Only the situations that need a decision or Neo is actively watching.</div>
            </div>
            <div style={{ ...styles.panelBadge, color: responseBuckets.needsDecision.length ? (themeMode === "dark" ? "#fb7185" : "#be123c") : styles.panelBadge.color }}>
              {responseBuckets.needsDecision.length} need decision
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: isCompact ? "12px 12px 0" : "14px 18px 0" }}>
            {[
              ["needsDecision", "Needs Decision", responseBuckets.needsDecision.length],
              ["watching", "Watching", responseBuckets.watching.length],
            ].map(([key, label, count]) => {
              const active = responseDeskTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setResponseDeskTab(key); setSelectedDutyId(""); }}
                  style={{
                    borderRadius: 999,
                    padding: "8px 12px",
                    border: active ? "1px solid rgba(79,156,255,.48)" : "1px solid rgba(148,163,184,.18)",
                    background: active ? "rgba(79,156,255,.16)" : "transparent",
                    color: active ? (themeMode === "dark" ? "#bfdbfe" : "#1d4ed8") : (themeMode === "dark" ? "#94a3b8" : "#64748b"),
                    fontWeight: 900,
                    fontSize: 11,
                    cursor: "pointer",
                  }}
                >
                  {label} <span style={{ opacity: .72 }}>{count}</span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setResponseHistoryOpen(true)}
              style={{ marginLeft: isCompact ? 0 : "auto", border: "1px solid rgba(148,163,184,.18)", background: "transparent", color: themeMode === "dark" ? "#94a3b8" : "#64748b", borderRadius: 999, padding: "8px 12px", fontWeight: 900, fontSize: 11, cursor: "pointer" }}
            >
              Response history →
            </button>
          </div>

          {(() => {
            const deskRows = (responseBuckets[responseDeskTab] || [])
              .filter((row) => responseDeskTab === "resolved" || ["due", "overdue", "escalated"].includes(dutyManagerState(row, now)))
              .sort((a, b) => dutyAgeMinutes(b, now) - dutyAgeMinutes(a, now))
              .slice(0, 5);

            if (deskRows.length === 0) {
              return (
                <div style={styles.emptyWrap}>
                  <div style={styles.emptyTitle}>
                    {responseDeskTab === "needsDecision" ? "No manager decisions waiting" : responseDeskTab === "watching" ? "Neo is not chasing anything right now" : "Nothing resolved in this view yet"}
                  </div>
                  <div>
                    {responseDeskTab === "needsDecision" ? "When Neo cannot safely resolve ownership or escalation, the decision will appear here." : "The Response Desk stays quiet when there is nothing useful to act on."}
                  </div>
                </div>
              );
            }

            return (
              <div style={{ ...styles.attentionList, paddingTop: 14 }}>
                {deskRows.map((item) => {
                  const state = dutyManagerState(item, now);
                  const selected = selectedDutyId === item.id;
                  const decision = item.responseDecision || item.managerDecision || {};
                  const requiredRole = item.requiredRole || item.dutyOwnerRole || item.ownerRole || item.assignedRole || "";
                  const recommendation = decision.recommendedOwnerName || decision.recommendedName || item.recommendedOwnerName || "";
                  const noOwner = !item.assignedToName && !item?.dueNotification?.recipientNames?.[0];
                  const decisionCopy = String(decision.reason || decision.message || "").trim();
                  return (
                    <div key={item.id} style={{ ...styles.attentionCard, cursor: "default" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={styles.dutyTitle}>{item.dutyLabel || item.label || "Duty"}</div>
                          <div style={styles.dutyMeta}>{item.standardName || "Standard"} • {item.storeName || storeName}</div>
                        </div>
                        <span style={{
                          ...styles.responseChip,
                          color: responseDeskTab === "needsDecision" ? "#fb7185" : responseDeskTab === "resolved" ? "#34d399" : "#fbbf24",
                        }}>
                          {responseDeskTab === "needsDecision" ? "Decision required" : responseDeskTab === "resolved" ? "Resolved" : (state === "escalated" ? "Neo chasing" : state === "overdue" ? "Overdue" : "Due now")}
                        </span>
                      </div>

                      <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.55 }}>
                        {responseDeskTab === "needsDecision" ? (
                          <>
                            <strong>{noOwner ? "Owner unavailable." : "Management decision required."}</strong>{" "}
                            {decisionCopy || (requiredRole ? `This duty requires ${requiredRole}. Neo needs management to confirm the next responsible person.` : "Neo cannot safely resolve this situation automatically.")}
                          </>
                        ) : responseDeskTab === "watching" ? (
                          <>Neo is handling this response. {managerDutyTimeLabel(item, now)}{item.assignedToName ? ` • ${item.assignedToName} owns the duty.` : ""}</>
                        ) : (
                          <>Completed{item.completedByName ? ` by ${item.completedByName}` : ""}. No manager action required.</>
                        )}
                      </div>

                      {recommendation && responseDeskTab === "needsDecision" ? (
                        <div style={{ marginTop: 10, padding: "9px 11px", borderRadius: 12, background: "rgba(52,211,153,.07)", border: "1px solid rgba(52,211,153,.18)", fontSize: 12 }}>
                          <strong>Neo recommends:</strong> {recommendation}
                        </div>
                      ) : null}

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                        <button
                          type="button"
                          onClick={() => setSelectedDutyId(selected ? "" : item.id)}
                          style={{ border: "1px solid rgba(148,163,184,.22)", background: themeMode === "dark" ? "rgba(15,23,42,.62)" : "rgba(241,245,249,.9)", color: themeMode === "dark" ? "#dbeafe" : "#334155", borderRadius: 999, padding: "8px 12px", fontWeight: 900, fontSize: 11, cursor: "pointer" }}
                        >
                          {selected ? "Hide details" : "Details"}
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/manager-reports", { state: { fromNeoDuty: true, neoDutyId: item.id, sectionId: item.staffDashboardSectionId || item.standardId || "", standardId: item.standardId || item.staffDashboardSectionId || "", sectionName: item.standardName || item.sectionName || "", dutyId: item.dutyId || "", dutyLabel: item.dutyLabel || item.label || "", dueAt: item.dueAt || null } })}
                          style={{ border: "1px solid rgba(79,156,255,.34)", background: "rgba(79,156,255,.13)", color: themeMode === "dark" ? "#93c5fd" : "#2563eb", borderRadius: 999, padding: "8px 12px", fontWeight: 900, fontSize: 11, cursor: "pointer" }}
                        >
                          Open duty →
                        </button>
                      </div>

                      {selected ? (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(123,186,255,.14)", display: "grid", gap: 5, fontSize: 11, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>
                          <div>{managerDutyTimeLabel(item, now)}</div>
                          {requiredRole ? <div>Required role: {requiredRole}</div> : null}
                          <div>WhatsApp: {item?.dueNotification?.status || "not sent"}</div>
                          <div>Management: {item?.managerEscalation?.status || item.escalationStatus || "not escalated"}</div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {(responseBuckets[responseDeskTab] || []).length > 5 ? (
                  <div style={{ textAlign: "center", fontSize: 11, color: themeMode === "dark" ? "#94a3b8" : "#64748b", padding: "2px 0 6px" }}>
                    Showing the 5 highest-priority items. Full history remains available in Reports.
                  </div>
                ) : null}
              </div>
            );
          })()}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 16 }}>
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <div style={{ color: themeMode === "dark" ? "#60a5fa" : "#2563eb", fontSize: 11, fontWeight: 950, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 5 }}>Operational health</div>
                <h2 style={{ ...styles.panelTitle, fontSize: 22 }}>Response by Area</h2>
                <div style={styles.panelSub}>See where the operation is healthy and where Neo is actively responding.</div>
              </div>
            </div>
            <div style={styles.attentionList}>
              {areaIntelligence.length === 0 ? (
                <div style={styles.emptyWrap}>No operational activity in this view yet.</div>
              ) : areaIntelligence.map((area) => (
                <button key={area.key} type="button" onClick={() => setResponseFilter(area.key)} style={{ ...styles.responseCard, width: "100%", textAlign: "left", cursor: "pointer", color: "inherit" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                    <div>
                      <div style={styles.dutyTitle}>{area.label}</div>
                      <div style={{ ...styles.dutyMeta, marginTop: 4 }}>{area.completed}/{area.total} completed{area.live ? ` • ${area.live} active response${area.live === 1 ? "" : "s"}` : ""}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 12, fontWeight: 900, color: themeMode === "dark" ? area.tone : (area.health === "At risk" ? "#be123c" : area.health === "Attention" ? "#a16207" : area.health === "Healthy" ? "#047857" : "#475569") }}>● {area.health}</div>
                      <div style={{ ...styles.responseMetricValue, fontSize: 20, marginTop: 3, color: themeMode === "dark" ? area.tone : (area.health === "At risk" ? "#be123c" : area.health === "Attention" ? "#a16207" : area.health === "Healthy" ? "#047857" : "#475569") }}>{area.responseRate == null ? "—" : `${area.responseRate}%`}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <div style={{ color: themeMode === "dark" ? "#60a5fa" : "#2563eb", fontSize: 11, fontWeight: 950, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 5 }}>People intelligence</div>
                <h2 style={{ ...styles.panelTitle, fontSize: 22 }}>People Requiring Attention</h2>
                <div style={styles.panelSub}>Only staff with a current response concern are surfaced here.</div>
              </div>
            </div>
            <div style={styles.attentionList}>
              {peopleIntelligence.length === 0 ? (
                <div style={styles.emptyWrap}>✓ No staff response concerns right now.</div>
              ) : peopleIntelligence.map((person) => (
                <div key={person.name} style={styles.responseCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                    <div>
                      <div style={styles.dutyTitle}>{person.name}</div>
                      <div style={{ ...styles.dutyMeta, marginTop: 4 }}>
                        {person.decisions ? `${person.decisions} decision${person.decisions === 1 ? "" : "s"} waiting` : person.watching ? `${person.watching} active response${person.watching === 1 ? "" : "s"}` : `${person.completed}/${person.total} completed`}
                        {person.escalated ? ` • ${person.escalated} escalated` : ""}{person.late ? ` • ${person.late} late` : ""}
                      </div>
                    </div>
                    <div style={{ ...styles.responseMetricValue, fontSize: 22, color: person.decisions || person.escalated ? (themeMode === "dark" ? "#fb7185" : "#be123c") : person.responseRate >= 80 ? (themeMode === "dark" ? "#34d399" : "#047857") : (themeMode === "dark" ? "#fbbf24" : "#a16207") }}>{person.responseRate}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>


        <div style={styles.panel}>
          <div style={{ ...styles.panelHeader, alignItems: "center" }}>
            <div>
              <div style={{ color: themeMode === "dark" ? "#60a5fa" : "#2563eb", fontSize: 11, fontWeight: 950, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 5 }}>Recent activity</div>
              <h2 style={{ ...styles.panelTitle, fontSize: 22 }}>What just happened</h2>
              <div style={styles.panelSub}>A short operational trail. Full response history stays one click away.</div>
            </div>
            <button type="button" onClick={() => setResponseHistoryOpen(true)} style={{ border: "1px solid rgba(79,156,255,.28)", background: "rgba(79,156,255,.10)", color: themeMode === "dark" ? "#93c5fd" : "#2563eb", borderRadius: 999, padding: "9px 13px", fontWeight: 900, fontSize: 11, cursor: "pointer" }}>View response history →</button>
          </div>
          <div style={{ padding: isCompact ? "0 12px 14px" : "0 18px 16px", display: "grid", gap: 0 }}>
            {recentResponseActivity.slice(0, 5).length ? recentResponseActivity.slice(0, 5).map((event, index) => (
              <div key={event.id} style={{ display: "grid", gridTemplateColumns: isMobile ? "58px minmax(0,1fr)" : isCompact ? "70px minmax(0,1fr)" : "86px minmax(0,1fr) auto", gap: 12, alignItems: "center", padding: "11px 2px", borderTop: index ? "1px solid rgba(148,163,184,.10)" : "none" }}>
                <div style={{ fontSize: 11, fontWeight: 900, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>{event.at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                <div style={{ minWidth: 0 }}><div style={{ fontWeight: 900, fontSize: 13 }}>{event.label}</div><div style={{ fontSize: 11, color: themeMode === "dark" ? "#94a3b8" : "#64748b", marginTop: 2 }}>{event.detail}</div></div>
                {!isCompact ? <div style={{ fontSize: 11, fontWeight: 900, color: event.tone }}>{event.type}</div> : null}
              </div>
            )) : <div style={{ padding: "18px 0", fontSize: 12, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>No recent response activity.</div>}
          </div>
        </div>

        {responseHistoryOpen ? (
          <div onClick={() => setResponseHistoryOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(2,6,23,.72)", display: "flex", justifyContent: "flex-end" }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: isMobile ? "100%" : 520, maxWidth: "100%", height: "100%", overflowY: "auto", background: themeMode === "dark" ? "#071426" : "#f8fafc", borderLeft: "1px solid rgba(79,156,255,.22)", boxShadow: "-18px 0 50px rgba(0,0,0,.28)" }}>
              <div style={{ position: "sticky", top: 0, zIndex: 2, padding: "20px 22px", background: themeMode === "dark" ? "rgba(7,20,38,.96)" : "rgba(248,250,252,.96)", borderBottom: "1px solid rgba(148,163,184,.14)", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <div><div style={{ color: themeMode === "dark" ? "#60a5fa" : "#2563eb", fontSize: 10, fontWeight: 950, letterSpacing: ".14em", textTransform: "uppercase" }}>Response history</div><div style={{ fontSize: 24, fontWeight: 950, marginTop: 4 }}>Operational trail</div></div>
                <button type="button" onClick={() => setResponseHistoryOpen(false)} style={{ width: 38, height: 38, borderRadius: 999, border: "1px solid rgba(148,163,184,.2)", background: "transparent", color: "inherit", fontSize: 20, cursor: "pointer" }}>×</button>
              </div>
              <div style={{ padding: 18, display: "grid", gap: 10 }}>
                {recentResponseActivity.length ? recentResponseActivity.map((event) => (
                  <div key={event.id} style={{ padding: 14, borderRadius: 16, border: "1px solid rgba(148,163,184,.14)", background: themeMode === "dark" ? "rgba(19,43,72,.72)" : "#fff" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}><strong>{event.label}</strong><span style={{ fontSize: 11, fontWeight: 900, color: event.tone }}>{event.type}</span></div>
                    <div style={{ marginTop: 5, fontSize: 12, color: themeMode === "dark" ? "#bfdbfe" : "#475569" }}>{event.detail}</div>
                    <div style={{ marginTop: 7, fontSize: 10, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>{event.at.toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                )) : <div style={styles.emptyWrap}>No response history in this operational view yet.</div>}
                <button type="button" onClick={() => navigate("/manager-reports")} style={{ marginTop: 4, border: "1px solid rgba(79,156,255,.34)", background: "rgba(79,156,255,.13)", color: themeMode === "dark" ? "#93c5fd" : "#2563eb", borderRadius: 14, padding: "12px 14px", fontWeight: 900, cursor: "pointer" }}>Open full Reports →</button>
              </div>
            </div>
          </div>
        ) : null}


</>) : null}

        {managerTab === "schedule" ? (
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <div style={styles.eyebrow}>WORKFORCE INTELLIGENCE</div>
                <h2 style={styles.panelTitle}>Today’s Schedule</h2>
                <div style={styles.panelSub}>Published schedule versus actual attendance from Neo Admin Schedule Intelligence.</div>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div style={styles.panelBadge}>Workforce Intelligence</div>
                <button
                  type="button"
                  onClick={openCurrentSchedule}
                  style={{
                    border: "1px solid rgba(52,211,153,.34)",
                    background: themeMode === "dark" ? "rgba(16,185,129,.10)" : "#ffffff",
                    color: themeMode === "dark" ? "#6ee7b7" : "#047857",
                    borderRadius: 999,
                    padding: "10px 14px",
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  View Current Schedule
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleHistoryOpen(true)}
                  style={{
                    border: "1px solid rgba(96,165,250,.34)",
                    background: themeMode === "dark" ? "rgba(59,130,246,.10)" : "#ffffff",
                    color: themeMode === "dark" ? "#bfdbfe" : "#1d4ed8",
                    borderRadius: 999,
                    padding: "10px 14px",
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  Decision History
                </button>
              </div>
            </div>

            {(() => {
              const openIssues = scheduleIssues.filter((issue) => !["resolved", "closed"].includes(String(issue.status || "open").toLowerCase()));
              if (!scheduleIssuesLoading && openIssues.length === 0) return null;
              const statusMeta = (status) => {
                const value = String(status || "open").toLowerCase();
                if (value === "acknowledged" || value === "reviewing") return { label: "Under review", tone: "#60a5fa" };
                return { label: "Open", tone: "#fbbf24" };
              };
              return (
                <div style={{ margin: "0 0 18px", padding: 16, borderRadius: 18, border: "1px solid rgba(251,191,36,.24)", background: themeMode === "dark" ? "rgba(120,53,15,.08)" : "rgba(255,251,235,.8)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ ...styles.eyebrow, color: themeMode === "dark" ? "#fbbf24" : "#a16207" }}>SCHEDULE ISSUES</div>
                      <div style={{ fontSize: 21, fontWeight: 950, marginTop: 4 }}>Staff reports requiring review</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ border: "1px solid rgba(251,191,36,.34)", background: "rgba(251,191,36,.12)", color: themeMode === "dark" ? "#fbbf24" : "#a16207", borderRadius: 999, padding: "7px 10px", fontSize: 11, fontWeight: 950 }}>
                        {scheduleIssuesLoading ? "Loading…" : `${openIssues.length} OPEN`}
                      </span>
                      <button type="button" onClick={loadScheduleIssues} disabled={scheduleIssuesLoading} style={{ border: "1px solid rgba(148,163,184,.22)", background: "transparent", color: "inherit", borderRadius: 999, padding: "7px 10px", fontSize: 11, fontWeight: 900, cursor: "pointer" }}>Refresh</button>
                    </div>
                  </div>
                  {scheduleIssuesLoading ? (
                    <div style={{ marginTop: 12, fontSize: 12, opacity: .72 }}>Loading schedule issues…</div>
                  ) : (
                    <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
                      {openIssues.map((issue) => {
                        const meta = statusMeta(issue.status);
                        return (
                          <div key={issue.id} style={{ border: "1px solid rgba(148,163,184,.16)", borderRadius: 16, padding: 14, background: themeMode === "dark" ? "rgba(15,23,42,.34)" : "#fff" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                              <div>
                                <div style={{ fontSize: 16, fontWeight: 950 }}>{issue.staffName || "Staff member"} <span style={{ fontSize: 12, fontWeight: 700, opacity: .65 }}>· {issue.role || "Role not set"}</span></div>
                                <div style={{ marginTop: 4, fontSize: 12, opacity: .76 }}>{issue.date || "—"} · {issue.scheduledLabel || "Shift not set"}</div>
                              </div>
                              <span style={{ color: meta.tone, border: `1px solid ${meta.tone}55`, background: `${meta.tone}14`, borderRadius: 999, padding: "6px 9px", fontSize: 10, fontWeight: 950 }}>{meta.label}</span>
                            </div>
                            <div style={{ marginTop: 10, fontWeight: 900 }}>{issue.reason || "Schedule issue"}</div>
                            {issue.note ? <div style={{ marginTop: 5, fontSize: 12, lineHeight: 1.5, opacity: .82 }}>{issue.note}</div> : null}
                            <textarea
                              value={scheduleIssueResponseDrafts[issue.id] || ""}
                              onChange={(event) => setScheduleIssueResponseDrafts((drafts) => ({ ...drafts, [issue.id]: event.target.value }))}
                              placeholder="Manager response to staff (optional)"
                              rows={2}
                              style={{ width: "100%", boxSizing: "border-box", marginTop: 11, resize: "vertical", borderRadius: 12, border: "1px solid rgba(148,163,184,.22)", background: themeMode === "dark" ? "rgba(2,8,23,.42)" : "#fff", color: "inherit", padding: "10px 11px", font: "inherit", fontSize: 12 }}
                            />
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                              {String(issue.status || "open").toLowerCase() === "open" ? (
                                <button type="button" onClick={() => updateScheduleIssueStatus(issue, "acknowledged")} style={{ border: "1px solid rgba(96,165,250,.32)", background: "rgba(59,130,246,.10)", color: themeMode === "dark" ? "#93c5fd" : "#1d4ed8", borderRadius: 999, padding: "8px 11px", fontSize: 11, fontWeight: 900, cursor: "pointer" }}>Acknowledge</button>
                              ) : null}
                              <button type="button" onClick={() => openScheduleIssueDate(issue)} style={{ border: "1px solid rgba(52,211,153,.30)", background: "rgba(16,185,129,.09)", color: themeMode === "dark" ? "#6ee7b7" : "#047857", borderRadius: 999, padding: "8px 11px", fontSize: 11, fontWeight: 900, cursor: "pointer" }}>Open Schedule →</button>
                              <button type="button" onClick={() => updateScheduleIssueStatus(issue, "resolved")} style={{ border: "1px solid rgba(52,211,153,.34)", background: "rgba(16,185,129,.14)", color: themeMode === "dark" ? "#6ee7b7" : "#047857", borderRadius: 999, padding: "8px 11px", fontSize: 11, fontWeight: 950, cursor: "pointer" }}>Resolve ✓</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {scheduleLoading ? (
              <div style={styles.emptyWrap}><div style={styles.emptyTitle}>Loading today’s schedule…</div></div>
            ) : scheduleRows.length === 0 ? (
              <div style={styles.emptyWrap}><div style={styles.emptyTitle}>No published schedule for today</div><div>Publish today’s roster in Neo Admin Schedule Intelligence to make it available here.</div></div>
            ) : (
              <div style={styles.attentionList}>
                {scheduleRows.map((row)=>{
                  const actual=row.actual||null;
                  const actualIn=actual?.clockInSec?new Date(actual.clockInSec*1000):null;
                  const actualOut=actual?.clockOutSec?new Date(actual.clockOutSec*1000):null;
                  const actualText=actualIn?`${formatClockTime(actualIn)} → ${actualOut?formatClockTime(actualOut):"On shift"}`:"Not clocked in";
                  const intel=row.intelligence||{label:"Awaiting intelligence",tone:"#94a3b8"};
                  const role=actual?.roleName||actual?.role||row.roleName||row.role||"Role not set";
                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => setSelectedScheduleRowId(row.id)}
                      style={{
                        ...styles.responseCard,
                        width: "100%",
                        textAlign: "left",
                        color: "inherit",
                        cursor: "pointer",
                        font: "inherit",
                      }}
                    >
                      <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                        <div><div style={styles.dutyTitle}>{row.staffName||actual?.staffName||actual?.name||"Staff member"}</div><div style={styles.dutyMeta}>{role}</div></div>
                        <span style={{...styles.responseChip,color:intel.tone,borderColor:`${intel.tone}55`,background:`${intel.tone}14`}}>{intel.label}</span>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:isCompact?"1fr":"repeat(3,minmax(0,1fr))",gap:10,marginTop:12}}>
                        <div><div style={styles.responseMetricLabel}>Scheduled</div><div style={{marginTop:4,fontWeight:800}}>{row.scheduledText||"—"}</div></div>
                        <div><div style={styles.responseMetricLabel}>Actual</div><div style={{marginTop:4,fontWeight:800}}>{actualText}</div></div>
                        <div><div style={styles.responseMetricLabel}>Neo Intelligence</div><div
                          style={{
                            marginTop: 4,
                            fontWeight: 900,
                            color: String(intel.label || "").toLowerCase().includes("approval required")
                              ? "#f59e0b"
                              : intel.tone,
                          }}
                        >
                          {intel.label}
                        </div></div>
                      </div>
                      <div
                        style={{
                          marginTop: 12,
                          paddingTop: 10,
                          borderTop: "1px solid rgba(148,163,184,.14)",
                          fontSize: 12,
                          fontWeight: 800,
                          color: themeMode === "dark" ? "#93c5fd" : "#2563eb",
                        }}
                      >
                        Open staff schedule intelligence →
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}




        {currentScheduleOpen ? (
          <div
            role="dialog"
            aria-modal="true"
            onClick={() => setCurrentScheduleOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1260,
              background: "rgba(2,8,23,.64)",
              backdropFilter: "blur(5px)",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width: isCompact ? "100%" : 640,
                height: "100%",
                overflowY: "auto",
                background: themeMode === "dark" ? "#08182d" : "#f7fbff",
                borderLeft: themeMode === "dark"
                  ? "1px solid rgba(52,211,153,.22)"
                  : "1px solid rgba(5,150,105,.14)",
                boxShadow: "-24px 0 60px rgba(2,8,23,.28)",
                padding: isCompact ? 20 : 26,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" }}>
                <div>
                  <div style={styles.eyebrow}>CURRENT PUBLISHED ROSTER</div>
                  <h2 style={{ ...styles.panelTitle, marginTop: 6 }}>Current Schedule</h2>
                  <div style={styles.panelSub}>
                    Read-only team schedule from Neo Admin Schedule Intelligence.
                  </div>
                </div>
                <button
                  type="button"
                  aria-label="Close current schedule"
                  onClick={() => {
                    currentScheduleRequestedDateRef.current = "";
                    setCurrentScheduleOpen(false);
                  }}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 14,
                    border: "1px solid rgba(148,163,184,.22)",
                    background: "transparent",
                    color: themeMode === "dark" ? "#e2e8f0" : "#334155",
                    fontSize: 22,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>

              {currentScheduleLoading ? (
                <div style={{ ...styles.emptyWrap, marginTop: 20 }}>
                  <div style={styles.emptyTitle}>Loading current schedule…</div>
                </div>
              ) : currentScheduleEntries.length === 0 ? (
                <div style={{ ...styles.emptyWrap, marginTop: 20 }}>
                  <div style={styles.emptyTitle}>No published schedule this month</div>
                  <div>Publish the current roster in Neo Admin Schedule Intelligence to show it here.</div>
                </div>
              ) : (
                <>
                  <div
                    ref={currentScheduleDateStripRef}
                    style={{
                      marginTop: 20,
                      display: "flex",
                      gap: 8,
                      overflowX: "auto",
                      paddingBottom: 8,
                      scrollBehavior: "smooth",
                    }}
                  >
                    {currentScheduleDates.map((date) => {
                      const parsed = new Date(`${date}T12:00:00`);
                      const selected = date === currentScheduleSelectedDate;
                      const today = date === managerTodayKey(new Date());

                      return (
                        <button
                          key={date}
                          data-neo-schedule-date={date}
                          type="button"
                          onClick={() => {
                            currentScheduleRequestedDateRef.current = "";
                            setCurrentScheduleSelectedDate(date);
                          }}
                          style={{
                            minWidth: 74,
                            borderRadius: 16,
                            border: selected
                              ? "1px solid rgba(52,211,153,.60)"
                              : "1px solid rgba(148,163,184,.18)",
                            background: selected
                              ? "rgba(52,211,153,.14)"
                              : themeMode === "dark"
                              ? "rgba(15,35,62,.72)"
                              : "#ffffff",
                            color: selected
                              ? (themeMode === "dark" ? "#6ee7b7" : "#047857")
                              : "inherit",
                            padding: "10px 12px",
                            cursor: "pointer",
                            fontWeight: 900,
                            flex: "0 0 auto",
                          }}
                        >
                          <div style={{ fontSize: 10, letterSpacing: ".08em", textTransform: "uppercase", opacity: .72 }}>
                            {parsed.toLocaleDateString([], { weekday: "short" })}
                          </div>
                          <div style={{ fontSize: 20, marginTop: 2 }}>
                            {parsed.getDate()}
                          </div>
                          {today ? (
                            <div style={{ fontSize: 9, marginTop: 2 }}>TODAY</div>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>

                  <div
                    style={{
                      marginTop: 12,
                      padding: "14px 16px",
                      borderRadius: 16,
                      border: "1px solid rgba(148,163,184,.16)",
                      background: themeMode === "dark" ? "rgba(15,35,62,.68)" : "#ffffff",
                    }}
                  >
                    <div style={styles.responseMetricLabel}>Selected day</div>
                    <div style={{ marginTop: 5, fontSize: 18, fontWeight: 900 }}>
                      {currentScheduleSelectedDate
                        ? new Date(`${currentScheduleSelectedDate}T12:00:00`).toLocaleDateString([], {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                          })
                        : "—"}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 12, opacity: .72 }}>
                      {currentScheduleDayRows.length} scheduled {currentScheduleDayRows.length === 1 ? "person" : "people"}
                    </div>
                  </div>

                  <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
                    {currentScheduleDayRows.length === 0 ? (
                      <div style={styles.emptyWrap}>No one is scheduled for this day.</div>
                    ) : (
                      currentScheduleDayRows.map((row) => {
                        const window = scheduleWindow(row);
                        const shiftText =
                          row.publishedShift ||
                          row.shift ||
                          (window.startTime && window.endTime
                            ? `${window.startTime}-${window.endTime}`
                            : row.off === true || row.isOff === true
                            ? "OFF"
                            : "Scheduled");

                        return (
                          <div
                            key={row.id}
                            style={{
                              border: "1px solid rgba(148,163,184,.16)",
                              background: themeMode === "dark" ? "rgba(15,35,62,.72)" : "#ffffff",
                              borderRadius: 18,
                              padding: 16,
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 14,
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <div style={{ fontSize: 16, fontWeight: 900 }}>
                                {row.staffName || "Staff member"}
                              </div>
                              <div style={{ marginTop: 4, fontSize: 12, opacity: .72 }}>
                                {row.roleName || row.role || "Role not set"}
                              </div>
                            </div>
                            <div
                              style={{
                                textAlign: "right",
                                fontSize: 15,
                                fontWeight: 900,
                                color:
                                  String(shiftText).toUpperCase() === "OFF"
                                    ? "#fbbf24"
                                    : themeMode === "dark"
                                    ? "#67e8f9"
                                    : "#0369a1",
                              }}
                            >
                              {shiftText}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div
                    style={{
                      marginTop: 18,
                      padding: 14,
                      borderRadius: 16,
                      border: "1px dashed rgba(52,211,153,.28)",
                      color: themeMode === "dark" ? "#a7f3d0" : "#047857",
                      fontSize: 12,
                      lineHeight: 1.5,
                    }}
                  >
                    Read-only preview. Schedule creation, rules, drafts and publishing remain in Neo Admin.
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}

        {scheduleHistoryOpen ? (
          <div
            role="dialog"
            aria-modal="true"
            onClick={() => setScheduleHistoryOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1250,
              background: "rgba(2,8,23,.64)",
              backdropFilter: "blur(5px)",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width: isCompact ? "100%" : 560,
                height: "100%",
                overflowY: "auto",
                background: themeMode === "dark" ? "#08182d" : "#f7fbff",
                borderLeft: themeMode === "dark"
                  ? "1px solid rgba(96,165,250,.24)"
                  : "1px solid rgba(37,99,235,.14)",
                boxShadow: "-24px 0 60px rgba(2,8,23,.28)",
                padding: isCompact ? 20 : 26,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" }}>
                <div>
                  <div style={styles.eyebrow}>SHARED SCHEDULE HISTORY</div>
                  <h2 style={{ ...styles.panelTitle, marginTop: 6 }}>Today’s decisions</h2>
                  <div style={styles.panelSub}>
                    The same decision trail used by Neo Admin Schedule Intelligence.
                  </div>
                </div>
                <button
                  type="button"
                  aria-label="Close schedule decision history"
                  onClick={() => setScheduleHistoryOpen(false)}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 14,
                    border: "1px solid rgba(148,163,184,.22)",
                    background: "transparent",
                    color: themeMode === "dark" ? "#e2e8f0" : "#334155",
                    fontSize: 22,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>

              <button
                type="button"
                onClick={() => loadScheduleDecisionHistory()}
                disabled={scheduleHistoryLoading}
                style={{
                  marginTop: 18,
                  width: "100%",
                  border: "1px solid rgba(96,165,250,.32)",
                  background: themeMode === "dark" ? "rgba(59,130,246,.10)" : "#ffffff",
                  color: themeMode === "dark" ? "#bfdbfe" : "#1d4ed8",
                  borderRadius: 14,
                  padding: "12px 14px",
                  fontWeight: 900,
                  cursor: scheduleHistoryLoading ? "wait" : "pointer",
                }}
              >
                {scheduleHistoryLoading ? "Refreshing…" : "Refresh history"}
              </button>

              <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
                {scheduleHistoryLoading && scheduleHistoryRows.length === 0 ? (
                  <div style={styles.emptyWrap}>Loading today’s schedule decisions…</div>
                ) : scheduleHistoryRows.length === 0 ? (
                  <div style={styles.emptyWrap}>
                    <div style={styles.emptyTitle}>No schedule decisions today</div>
                    <div>Approvals and rejections made in Neo App or Neo Admin will appear here.</div>
                  </div>
                ) : (
                  scheduleHistoryRows.map((item) => {
                    const approved = String(item.decision || "").toLowerCase() === "approved";
                    const tone = approved ? "#34d399" : "#fb7185";
                    return (
                      <div
                        key={item.id}
                        style={{
                          border: "1px solid rgba(148,163,184,.16)",
                          background: themeMode === "dark" ? "rgba(15,35,62,.72)" : "#ffffff",
                          borderRadius: 18,
                          padding: 16,
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                          <div>
                            <div style={{ fontWeight: 900, fontSize: 17 }}>
                              {item.staffName || "Staff member"}
                            </div>
                            <div style={{ marginTop: 4, fontSize: 12, opacity: .72 }}>
                              {item.varianceLabel || "Schedule exception"}
                            </div>
                          </div>
                          <span style={{
                            border: `1px solid ${tone}55`,
                            background: `${tone}14`,
                            color: tone,
                            borderRadius: 999,
                            padding: "7px 10px",
                            fontSize: 11,
                            fontWeight: 900,
                          }}>
                            {approved ? "Approved" : "Rejected"}
                          </span>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                            gap: 10,
                            marginTop: 14,
                          }}
                        >
                          <div>
                            <div style={styles.responseMetricLabel}>Published</div>
                            <div style={{ marginTop: 4, fontWeight: 800 }}>{item.before || "—"}</div>
                          </div>
                          <div>
                            <div style={styles.responseMetricLabel}>Decision result</div>
                            <div style={{ marginTop: 4, fontWeight: 800 }}>{item.after || "—"}</div>
                          </div>
                        </div>

                        <div
                          style={{
                            marginTop: 14,
                            paddingTop: 12,
                            borderTop: "1px solid rgba(148,163,184,.14)",
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 10,
                            flexWrap: "wrap",
                            fontSize: 12,
                            opacity: .78,
                          }}
                        >
                          <span>{item.managerName || "Manager"} · {item.source || "Neo"}</span>
                          <span>{item.timeLabel || ""}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        ) : null}

        {selectedScheduleRow ? (() => {
          const row = selectedScheduleRow;
          const actual = row.actual || null;
          const actualIn = actual?.clockInSec ? new Date(actual.clockInSec * 1000) : null;
          const actualOut = actual?.clockOutSec ? new Date(actual.clockOutSec * 1000) : null;
          const actualText = actualIn
            ? `${formatClockTime(actualIn)} → ${actualOut ? formatClockTime(actualOut) : "On shift"}`
            : "Not clocked in";
          const intel = row.intelligence || { label: "Awaiting intelligence", tone: "#94a3b8" };
          const role = actual?.roleName || actual?.role || row.roleName || row.role || "Role not set";
          const staffName = row.staffName || actual?.staffName || actual?.name || "Staff member";
          const isException = /approval required|overtime|late|early clock-out|not clocked in|unscheduled|off day/i.test(
            String(intel.label || "")
          );
          const decisionStatus =
            actual?.scheduleVarianceApproved === true
              ? "Approved"
              : actual?.scheduleVarianceRejected === true
              ? "Rejected"
              : isException
              ? "Pending review"
              : "No decision required";
          const managerVariance = buildManagerScheduleVariance({
            entries: managerVarianceEntries,
            shifts: managerVarianceShifts,
            staffName,
            now,
            defaultLunchMinutes: managerDefaultLunchMinutes,
          });
          const managerVarianceMinutes = Math.round(managerVariance.varianceHours * 60);
          const managerVarianceState = Math.abs(managerVarianceMinutes) <= 5 ? "on_schedule" : managerVarianceMinutes > 0 ? "ahead" : "behind";
          const managerVarianceLabel = managerVarianceState === "on_schedule" ? "On schedule" : `${managerFormatVarianceDuration(managerVariance.varianceHours)} ${managerVarianceState}`;
          const todayVarianceHours = managerVariance.todayCreditedHours - managerVariance.todayScheduledHours;
          const todayVarianceMinutes = Math.round(todayVarianceHours * 60);
          const todayVarianceState = Math.abs(todayVarianceMinutes) <= 5 ? "on_schedule" : todayVarianceMinutes > 0 ? "ahead" : "behind";
          const projectionGapHours = managerVariance.projectedMonthEndHours - managerMonthlyTarget;
          const projectionOnTrack = projectionGapHours >= -0.01;

          return (
            <div
              role="dialog"
              aria-modal="true"
              onClick={() => setSelectedScheduleRowId("")}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 1200,
                background: "rgba(2,8,23,.64)",
                backdropFilter: "blur(5px)",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <div
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: isCompact ? "100%" : 520,
                  height: "100%",
                  overflowY: "auto",
                  background: themeMode === "dark" ? "#08182d" : "#f7fbff",
                  borderLeft: themeMode === "dark"
                    ? "1px solid rgba(96,165,250,.24)"
                    : "1px solid rgba(37,99,235,.14)",
                  boxShadow: "-24px 0 60px rgba(2,8,23,.28)",
                  padding: isCompact ? 20 : 26,
                }}
              >
                <div
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 6,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 16,
                    alignItems: "flex-start",
                    paddingBottom: 14,
                    marginBottom: 2,
                    background: themeMode === "dark" ? "#08182d" : "#f7fbff",
                    borderBottom: themeMode === "dark"
                      ? "1px solid rgba(96,165,250,.14)"
                      : "1px solid rgba(37,99,235,.10)",
                  }}
                >
                  <div>
                    <div style={styles.eyebrow}>SCHEDULE INTELLIGENCE</div>
                    <h2 style={{ ...styles.panelTitle, marginTop: 6 }}>{staffName}</h2>
                    <div style={styles.panelSub}>{role} · {storeName || "Current store"}</div>
                  </div>
                  <button
                    type="button"
                    aria-label="Close schedule intelligence"
                    onClick={() => setSelectedScheduleRowId("")}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 14,
                      border: "1px solid rgba(148,163,184,.22)",
                      background: "transparent",
                      color: themeMode === "dark" ? "#e2e8f0" : "#334155",
                      fontSize: 22,
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>

                <div
                  style={{
                    marginTop: 22,
                    padding: 18,
                    borderRadius: 18,
                    border: `1px solid ${String(intel.label || "").toLowerCase().includes("approval required") ? "#f59e0b" : intel.tone}44`,
                    background: `${String(intel.label || "").toLowerCase().includes("approval required") ? "#f59e0b" : intel.tone}10`,
                  }}
                >
                  <div style={styles.responseMetricLabel}>Neo Intelligence</div>
                  <div style={{ marginTop: 7, fontSize: 20, fontWeight: 900, color: String(intel.label || "").toLowerCase().includes("approval required") ? "#f59e0b" : intel.tone }}>
                    {intel.label}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, fontWeight: 800, opacity: .72 }}>
                    {decisionStatus}
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                    marginTop: 16,
                  }}
                >
                  <div style={styles.responseMetricCard}>
                    <div style={styles.responseMetricLabel}>Published schedule</div>
                    <div style={styles.responseMetricValue}>{row.scheduledText || "—"}</div>
                  </div>
                  <div style={styles.responseMetricCard}>
                    <div style={styles.responseMetricLabel}>Actual attendance</div>
                    <div style={{ ...styles.responseMetricValue, fontSize: 16 }}>{actualText}</div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 16,
                    padding: 18,
                    borderRadius: 18,
                    border: "1px solid rgba(148,163,184,.16)",
                    background: themeMode === "dark" ? "rgba(15,35,62,.72)" : "#ffffff",
                  }}
                >
                  <div style={styles.responseMetricLabel}>Today’s exception</div>
                  {isException ? (
                    <>
                      <div style={{ marginTop: 8, fontWeight: 900 }}>{intel.label}</div>
                      <div style={{ marginTop: 7, fontSize: 13, lineHeight: 1.55, opacity: .78 }}>
                        This is the same attendance exception context used by Neo Admin Schedule Intelligence.
                      </div>
                    </>
                  ) : (
                    <div style={{ marginTop: 8, fontSize: 14 }}>
                      No schedule exception currently requires attention for this staff member.
                    </div>
                  )}
                </div>

                <div
                  style={{
                    marginTop: 16,
                    padding: 18,
                    borderRadius: 18,
                    border: "1px solid rgba(96,165,250,.22)",
                    background: themeMode === "dark" ? "rgba(10,30,54,.86)" : "#ffffff",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ ...styles.responseMetricLabel, color: themeMode === "dark" ? "#60a5fa" : "#2563eb" }}>NEO SCHEDULE INTELLIGENCE</div>
                      <div style={{ marginTop: 6, fontSize: 20, fontWeight: 950 }}>Schedule Variance</div>
                      <div style={{ marginTop: 3, fontSize: 12, opacity: .7, fontWeight: 700 }}>
                        {`${now.toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" })} - ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}`}
                      </div>
                    </div>
                    {!managerVarianceLoading && !managerVarianceError && managerVarianceEntries.length > 0 ? (
                      <div style={{
                        fontSize: 15,
                        fontWeight: 950,
                        color: managerVarianceState === "ahead" ? "#34d399" : managerVarianceState === "behind" ? "#fbbf24" : (themeMode === "dark" ? "#93c5fd" : "#2563eb"),
                      }}>
                        {managerVarianceState === "ahead" ? "↑ " : managerVarianceState === "behind" ? "↓ " : "✓ "}{managerVarianceLabel}
                      </div>
                    ) : null}
                  </div>

                  {managerVarianceLoading ? (
                    <div style={{ marginTop: 16, fontSize: 13, opacity: .72 }}>Calculating against {staffName}’s published schedule…</div>
                  ) : managerVarianceError ? (
                    <div style={{ marginTop: 16, fontSize: 13, color: "#fb7185" }}>{managerVarianceError}</div>
                  ) : managerVarianceEntries.length === 0 ? (
                    <div style={{ marginTop: 16, fontSize: 13, opacity: .72 }}>No published schedule found for this month.</div>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, marginTop: 16 }}>
                        <div style={{ padding: 14, borderRadius: 15, border: "1px solid rgba(148,163,184,.16)", background: themeMode === "dark" ? "rgba(7,20,38,.72)" : "#f8fbff" }}>
                          <div style={{ ...styles.responseMetricLabel, color: themeMode === "dark" ? "#60a5fa" : "#2563eb" }}>MONTH TO DATE</div>
                          {[
                            ["Scheduled to date", managerVariance.scheduledHoursToDate],
                            ["Regular credited", managerVariance.regularCreditedHoursToDate],
                            ["Approved overtime", managerVariance.approvedOvertimeHoursToDate],
                            ["Paid leave credit", managerVariance.paidLeaveHoursToDate],
                          ].map(([label, value]) => (
                            <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 0", fontSize: 12, opacity: .82 }}>
                              <span>{label}</span><strong>{managerFormatVarianceDuration(value)}</strong>
                            </div>
                          ))}
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 5, paddingTop: 10, borderTop: "1px solid rgba(148,163,184,.16)", fontSize: 13, fontWeight: 950 }}>
                            <span>Credited to date</span><strong>{managerFormatVarianceDuration(managerVariance.creditedHours)}</strong>
                          </div>
                        </div>

                        <div style={{ padding: 14, borderRadius: 15, border: "1px solid rgba(148,163,184,.16)", background: themeMode === "dark" ? "rgba(7,20,38,.72)" : "#f8fbff" }}>
                          <div style={{ ...styles.responseMetricLabel, color: themeMode === "dark" ? "#60a5fa" : "#2563eb" }}>TODAY · LIVE</div>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 0", fontSize: 12, opacity: .82 }}><span>Scheduled so far</span><strong>{managerFormatVarianceDuration(managerVariance.todayScheduledHours)}</strong></div>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 0", fontSize: 12, opacity: .82 }}><span>Credited so far</span><strong>{managerFormatVarianceDuration(managerVariance.todayCreditedHours)}</strong></div>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 5, paddingTop: 10, borderTop: "1px solid rgba(148,163,184,.16)", fontSize: 13, fontWeight: 950 }}>
                            <span>Today</span>
                            <strong>{todayVarianceState === "on_schedule" ? "On schedule" : `${managerFormatVarianceDuration(todayVarianceHours)} ${todayVarianceState}`}</strong>
                          </div>
                        </div>
                      </div>

                      <div style={{ marginTop: 12, padding: 13, borderRadius: 14, border: "1px dashed rgba(148,163,184,.22)", fontSize: 12, lineHeight: 1.5, fontWeight: 800 }}>
                        {managerVarianceState === "ahead"
                          ? `${staffName}’s approved credited time currently puts them ${managerFormatVarianceDuration(managerVariance.varianceHours)} ahead of their published schedule.`
                          : managerVarianceState === "behind"
                          ? `${staffName} is currently ${managerFormatVarianceDuration(managerVariance.varianceHours)} behind their published schedule to this point in the month.`
                          : `${staffName}’s credited time is currently aligned with their published schedule.`}
                      </div>

                      <div style={{ marginTop: 12, padding: 14, borderRadius: 15, border: "1px solid rgba(96,165,250,.18)", background: themeMode === "dark" ? "rgba(7,20,38,.62)" : "#f8fbff" }}>
                        <div style={{ ...styles.responseMetricLabel, color: themeMode === "dark" ? "#60a5fa" : "#2563eb" }}>TARGET PROJECTION</div>
                        <div style={{ marginTop: 7, fontSize: 21, fontWeight: 950 }}>{managerFormatVarianceDuration(managerVariance.projectedMonthEndHours)} projected</div>
                        <div style={{ marginTop: 4, fontSize: 13, fontWeight: 900, color: projectionOnTrack ? "#34d399" : "#fbbf24" }}>
                          {projectionOnTrack
                            ? `On track · ${managerFormatVarianceDuration(projectionGapHours)} above target`
                            : `${managerFormatVarianceDuration(projectionGapHours)} below target`}
                        </div>
                        <div style={{ display: "grid", gap: 7, marginTop: 12 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12, opacity: .82 }}><span>Published month schedule</span><strong>{managerFormatVarianceDuration(managerVariance.totalPublishedScheduledHours)}</strong></div>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12, opacity: .82 }}><span>Remaining scheduled</span><strong>{managerFormatVarianceDuration(managerVariance.remainingScheduledHours)}</strong></div>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12, opacity: .82 }}><span>Monthly target</span><strong>{managerFormatVarianceDuration(managerMonthlyTarget)}</strong></div>
                        </div>
                        <div style={{ marginTop: 11, fontSize: 11, lineHeight: 1.45, opacity: .68 }}>
                          Projection assumes {staffName} completes the rest of the currently published paid schedule. Future approved overtime, leave changes or roster edits will update this projection.
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {(() => {
                  const meta = scheduleDecisionMeta(row);
                  const decisionKey = String(row.id || "");
                  const state = scheduleDecisionState[decisionKey] || "";
                  const alreadyApproved =
                    actual?.scheduleVarianceApproved === true ||
                    row?.varianceApproved === true;
                  const alreadyRejected =
                    actual?.scheduleVarianceRejected === true ||
                    row?.varianceRejected === true;

                  if (alreadyApproved || state === "approved") {
                    return (
                      <div style={{
                        marginTop: 16,
                        padding: 16,
                        borderRadius: 16,
                        border: "1px solid rgba(52,211,153,.30)",
                        background: "rgba(52,211,153,.10)",
                        color: "#34d399",
                        fontWeight: 900,
                      }}>
                        ✓ Approved · synced with Neo Admin
                      </div>
                    );
                  }

                  if (alreadyRejected || state === "rejected") {
                    return (
                      <div style={{
                        marginTop: 16,
                        padding: 16,
                        borderRadius: 16,
                        border: "1px solid rgba(251,113,133,.30)",
                        background: "rgba(251,113,133,.10)",
                        color: "#fb7185",
                        fontWeight: 900,
                      }}>
                        Rejected · synced with Neo Admin
                      </div>
                    );
                  }

                  if (meta.decisionAvailableAfterClockOut) {
                    return (
                      <div style={{
                        marginTop: 16,
                        padding: 16,
                        borderRadius: 16,
                        border: "1px solid rgba(56,189,248,.28)",
                        background: "rgba(56,189,248,.08)",
                        color: themeMode === "dark" ? "#bae6fd" : "#075985",
                        fontWeight: 800,
                      }}>
                        Decision available after clock-out
                      </div>
                    );
                  }

                  if (!meta.actionable) {
                    return null;
                  }

                  return (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                      marginTop: 16,
                    }}>
                      <button
                        type="button"
                        disabled={state === "saving"}
                        onClick={() => decideScheduleVariance(row, true)}
                        style={{
                          border: "1px solid rgba(52,211,153,.44)",
                          borderRadius: 14,
                          padding: "14px 16px",
                          background: "rgba(52,211,153,.13)",
                          color: "#34d399",
                          fontWeight: 900,
                          cursor: state === "saving" ? "wait" : "pointer",
                          opacity: state === "saving" ? .6 : 1,
                        }}
                      >
                        {state === "saving" ? "Saving…" : meta.unscheduled ? "Approve shift" : "Approve overtime"}
                      </button>
                      <button
                        type="button"
                        disabled={state === "saving"}
                        onClick={() => decideScheduleVariance(row, false)}
                        style={{
                          border: "1px solid rgba(251,113,133,.44)",
                          borderRadius: 14,
                          padding: "14px 16px",
                          background: "rgba(251,113,133,.10)",
                          color: "#fb7185",
                          fontWeight: 900,
                          cursor: state === "saving" ? "wait" : "pointer",
                          opacity: state === "saving" ? .6 : 1,
                        }}
                      >
                        {state === "saving" ? "Saving…" : meta.unscheduled ? "Reject shift" : "Reject overtime"}
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })() : null}

        <div style={styles.actions}>
          <button
            type="button"
            onClick={() => navigate("/manager-reports")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 0 0 1px rgba(79,156,255,.22), 0 0 28px rgba(79,156,255,.18)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
            style={{ ...styles.actionButton, ...styles.reports }}
          >
            <div style={styles.actionTitle}>Reports</div>
            <div style={styles.actionSub}>View store submissions, exports, and report history.</div>
          </button>

          <button
            type="button"
            onClick={() => navigate("/manager-settings")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 0 0 1px rgba(79,156,255,.22), 0 0 28px rgba(20,184,166,.18)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
            style={{ ...styles.actionButton, ...styles.settings }}
          >
            <div style={styles.actionTitle}>Settings</div>
            <div style={styles.actionSub}>Manage staff, managers, roles, and store controls.</div>
          </button>

          <button
            type="button"
            onClick={logoutAndGoLogin}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 0 0 1px rgba(148,163,184,.22), 0 0 24px rgba(148,163,184,.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
            style={{ ...styles.actionButton, ...styles.back }}
          >
            <div style={styles.actionTitle}>Back</div>
            <div style={styles.actionSub}>Exit manager session and return to manager login.</div>
          </button>
        </div>
      </div>

      {selectedLeaveRequest ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Review leave request"
          onClick={() => { setSelectedLeaveId(""); setLeaveDecisionNote(""); }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2500,
            background: themeMode === "dark" ? "rgba(1,7,18,.76)" : "rgba(15,23,42,.42)",
            backdropFilter: "blur(7px)",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: isMobile ? "100%" : "min(560px, 92vw)",
              height: "100%",
              overflowY: "auto",
              background: themeMode === "dark" ? "#07162a" : "#f8fbff",
              color: themeMode === "dark" ? "#f4f7fb" : "#0f2747",
              borderLeft: themeMode === "dark" ? "1px solid rgba(123,186,255,.18)" : "1px solid rgba(150,174,205,.32)",
              boxShadow: themeMode === "dark" ? "-20px 0 60px rgba(0,0,0,.38)" : "-20px 0 55px rgba(24,55,96,.14)",
              padding: isMobile ? 18 : 28,
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ color: themeMode === "dark" ? "#c4b5fd" : "#6d28d9", fontSize: 11, fontWeight: 950, letterSpacing: ".14em", textTransform: "uppercase" }}>Leave Intelligence</div>
                <h2 style={{ margin: "8px 0 4px", fontSize: isMobile ? 26 : 34, lineHeight: 1.05 }}>{selectedLeaveRequest.staffName || "Staff member"}</h2>
                <div style={{ color: themeMode === "dark" ? "#94a3b8" : "#64748b", fontSize: 13 }}>{selectedLeaveRequest.staffRole || "Staff"} • {storeName}</div>
              </div>
              <button
                type="button"
                onClick={() => { setSelectedLeaveId(""); setLeaveDecisionNote(""); }}
                style={{ width: 42, height: 42, borderRadius: 14, border: themeMode === "dark" ? "1px solid rgba(148,163,184,.20)" : "1px solid rgba(15,23,42,.10)", background: "transparent", color: themeMode === "dark" ? "#e5edf7" : "#0f2747", cursor: "pointer", fontSize: 20 }}
              >×</button>
            </div>

            <div style={{ marginTop: 24, borderRadius: 20, padding: 18, border: themeMode === "dark" ? "1px solid rgba(167,139,250,.24)" : "1px solid rgba(109,40,217,.16)", background: themeMode === "dark" ? "rgba(124,58,237,.10)" : "rgba(109,40,217,.06)" }}>
              <div style={{ fontSize: 11, fontWeight: 950, letterSpacing: ".12em", textTransform: "uppercase", color: themeMode === "dark" ? "#c4b5fd" : "#6d28d9" }}>Leave request</div>
              <div style={{ marginTop: 8, fontSize: 23, fontWeight: 950, color: themeMode === "dark" ? "#ddd6fe" : "#5b21b6" }}>{leaveTypeLabel(selectedLeaveRequest.leaveType)}</div>
              <div style={{ marginTop: 7, color: themeMode === "dark" ? "#cbd5e1" : "#475569", fontWeight: 800 }}>{selectedLeaveRequest.startDate || "—"}{selectedLeaveRequest.endDate && selectedLeaveRequest.endDate !== selectedLeaveRequest.startDate ? ` → ${selectedLeaveRequest.endDate}` : ""}</div>
            </div>

            {selectedLeaveRequest.reason ? (
              <div style={{ marginTop: 16, borderRadius: 18, padding: 16, border: themeMode === "dark" ? "1px solid rgba(148,163,184,.16)" : "1px solid rgba(15,23,42,.08)", background: themeMode === "dark" ? "rgba(15,27,48,.72)" : "#ffffff" }}>
                <div style={{ fontSize: 11, fontWeight: 950, letterSpacing: ".12em", textTransform: "uppercase", color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>Staff note</div>
                <div style={{ marginTop: 8, lineHeight: 1.6 }}>{selectedLeaveRequest.reason}</div>
              </div>
            ) : null}

            <div style={{ marginTop: 16, borderRadius: 18, padding: 16, border: themeMode === "dark" ? "1px solid rgba(148,163,184,.16)" : "1px solid rgba(15,23,42,.08)", background: themeMode === "dark" ? "rgba(15,27,48,.72)" : "#ffffff" }}>
              <div style={{ fontSize: 11, fontWeight: 950, letterSpacing: ".12em", textTransform: "uppercase", color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>Supporting evidence</div>
              {selectedLeaveRequest.evidence?.url ? (
                <>
                  {String(selectedLeaveRequest.evidence?.type || "").startsWith("image/") ? (
                    <img src={selectedLeaveRequest.evidence.url} alt="Leave supporting evidence" style={{ width: "100%", maxHeight: 300, objectFit: "contain", marginTop: 12, borderRadius: 14, background: themeMode === "dark" ? "#020817" : "#eef2f7" }} />
                  ) : null}
                  <a href={selectedLeaveRequest.evidence.url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", marginTop: 12, color: themeMode === "dark" ? "#60a5fa" : "#2563eb", fontWeight: 900, textDecoration: "none" }}>Open evidence ↗</a>
                  <div style={{ marginTop: 5, fontSize: 12, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>{selectedLeaveRequest.evidence.name || "Supporting document"}</div>
                </>
              ) : (
                <div style={{ marginTop: 8, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>No supporting document attached.</div>
              )}
            </div>

            {String(selectedLeaveRequest.status || "pending").toLowerCase() === "pending" ? (
              <>
                <label style={{ display: "grid", gap: 7, marginTop: 16, fontSize: 12, fontWeight: 900, color: themeMode === "dark" ? "#cbd5e1" : "#475569" }}>
                  Manager note (optional)
                  <textarea
                    value={leaveDecisionNote}
                    onChange={(e) => setLeaveDecisionNote(e.target.value)}
                    placeholder="Add a note for the staff member"
                    style={{
                      minHeight: 90,
                      resize: "vertical",
                      borderRadius: 14,
                      border: themeMode === "dark" ? "1px solid rgba(148,163,184,.20)" : "1px solid rgba(15,23,42,.12)",
                      background: themeMode === "dark" ? "#0b1b31" : "#ffffff",
                      color: themeMode === "dark" ? "#f4f7fb" : "#0f2747",
                      padding: 12,
                      font: "inherit",
                      outline: "none",
                    }}
                  />
                </label>

                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, marginTop: 18, paddingBottom: 24 }}>
                  <button
                    type="button"
                    disabled={leaveDecisionState[selectedLeaveRequest.id] === "saving"}
                    onClick={() => decideLeaveRequest(selectedLeaveRequest, true)}
                    style={{ borderRadius: 14, border: themeMode === "dark" ? "1px solid rgba(52,211,153,.34)" : "1px solid rgba(4,120,87,.20)", background: themeMode === "dark" ? "rgba(16,185,129,.16)" : "rgba(16,185,129,.10)", color: themeMode === "dark" ? "#34d399" : "#047857", padding: 14, fontWeight: 950, cursor: "pointer" }}
                  >
                    {leaveDecisionState[selectedLeaveRequest.id] === "saving" ? "Saving…" : "Approve leave"}
                  </button>
                  <button
                    type="button"
                    disabled={leaveDecisionState[selectedLeaveRequest.id] === "saving"}
                    onClick={() => decideLeaveRequest(selectedLeaveRequest, false)}
                    style={{ borderRadius: 14, border: themeMode === "dark" ? "1px solid rgba(251,113,133,.34)" : "1px solid rgba(190,18,60,.20)", background: themeMode === "dark" ? "rgba(244,63,94,.14)" : "rgba(244,63,94,.08)", color: themeMode === "dark" ? "#fb7185" : "#be123c", padding: 14, fontWeight: 950, cursor: "pointer" }}
                  >
                    {leaveDecisionState[selectedLeaveRequest.id] === "saving" ? "Saving…" : "Reject leave"}
                  </button>
                </div>
              </>
            ) : (
              <div style={{
                marginTop: 16,
                padding: 16,
                borderRadius: 16,
                border: themeMode === "dark" ? "1px solid rgba(148,163,184,.18)" : "1px solid rgba(15,23,42,.10)",
                background: themeMode === "dark" ? "rgba(15,27,48,.72)" : "rgba(248,250,252,.92)",
              }}>
                <div style={{ fontSize: 11, fontWeight: 950, letterSpacing: ".12em", textTransform: "uppercase", color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>Decision</div>
                <div style={{
                  marginTop: 8,
                  fontWeight: 950,
                  fontSize: 18,
                  color: String(selectedLeaveRequest.status || "").toLowerCase() === "approved"
                    ? (themeMode === "dark" ? "#34d399" : "#047857")
                    : (themeMode === "dark" ? "#fb7185" : "#be123c")
                }}>
                  {String(selectedLeaveRequest.status || "").toLowerCase() === "approved" ? "Approved" : "Rejected"}
                </div>
                {selectedLeaveRequest.managerNote ? (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 950, letterSpacing: ".12em", textTransform: "uppercase", color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>Manager note</div>
                    <div style={{ marginTop: 7, lineHeight: 1.55, color: themeMode === "dark" ? "#e2e8f0" : "#334155" }}>{selectedLeaveRequest.managerNote}</div>
                  </div>
                ) : null}
                {(selectedLeaveRequest.reviewedBy || selectedLeaveRequest.reviewedVia) ? (
                  <div style={{ marginTop: 14, fontSize: 12, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>
                    Reviewed by {selectedLeaveRequest.reviewedBy || "Manager"}
                    {selectedLeaveRequest.reviewedVia ? ` • ${selectedLeaveRequest.reviewedVia === "neo_manager_app" ? "Neo Manager" : selectedLeaveRequest.reviewedVia}` : ""}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      ) : null}

      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "info" })}
      />
    </div>
  );
}
